import { useState, useMemo, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useLayout } from "@/hooks/use-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useErrorDialog } from "@/hooks/use-error-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OrgChartModal } from "@/v2/components/org-chart-modal";
import {
  Settings,
  Layers,
  Users,
  List,
  GitBranch,
  Shield,
  Plus,
  Save,
  CheckCircle2,
  Loader2,
  Copy,
  Zap,
  Archive,
  Edit2,
  Check,
  KeyRound,
  Eye,
  EyeOff,
  Trash2,
  Download,
  BarChart3,
  TrendingUp,
  Target,
  AlertTriangle,
  ArrowRightLeft,
  RotateCcw,
  X,
  Bell,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Activity,
  Server,
  Clock,
  ExternalLink,
  CalendarClock,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Smartphone,
} from "lucide-react";
import type {
  GovernanceVersionRecord,
  AuthorityLaneRecord,
  AuthorityActorRecord,
  ReferenceListItemRecord,
  WorkflowRuleRecord,
  FieldPermissionRecord,
  TransitionPermissionRecord,
  TeamMemberRecord,
} from "@shared/v2/types";
import { V2_LANE_KEYS, V2_ROLE_KEYS, V2_PHASE_KEYS, V2_REFERENCE_LIST_KEYS, V2_STATUS_LABELS, V2_GOVERNANCE_CONTROLLED_DESTINATIONS } from "@shared/v2/constants";
import {
  PAGE_CARDS,
  SECTION_LABELS,
  SECTION_NOTES,
  ALWAYS_VISIBLE_DESTINATIONS,
  permissionsToCardLevels,
  cardLevelsToPermissions,
  summarizeRolePermissions,
  type PageCardLevel,
  type PageCardSection,
} from "@shared/v2/role-page-map";

import { useAuth } from "@/hooks/use-auth";

type AdminTab = "versions" | "lanes" | "actors" | "reference-lists" | "workflow-rules" | "field-permissions" | "transition-permissions" | "team-members" | "departments" | "user-accounts" | "deleted-items" | "kpi" | "roles-permissions" | "reminder-schedule" | "chairman-channels";

const TABS: { key: AdminTab; label: string; icon: typeof Settings; permission: string }[] = [
  { key: "kpi", label: "KPI Dashboard", icon: BarChart3, permission: "view_kpi_dashboard" },
  { key: "versions", label: "Versions", icon: Layers, permission: "manage_governance" },
  { key: "lanes", label: "Lanes", icon: GitBranch, permission: "manage_governance" },
  { key: "actors", label: "Actors", icon: Users, permission: "manage_governance" },
  { key: "reference-lists", label: "Reference Lists", icon: List, permission: "manage_governance" },
  { key: "workflow-rules", label: "Workflow Rules", icon: Settings, permission: "manage_governance" },
  { key: "field-permissions", label: "Field Permissions", icon: Shield, permission: "manage_governance" },
  { key: "transition-permissions", label: "Transition Permissions", icon: ArrowRightLeft, permission: "manage_governance" },
  { key: "team-members", label: "Org Chart", icon: Users, permission: "manage_team_members" },
  { key: "departments", label: "Departments", icon: GitBranch, permission: "manage_team_members" },
  { key: "user-accounts", label: "User Accounts", icon: KeyRound, permission: "manage_users" },
  { key: "roles-permissions", label: "Roles & Permissions", icon: Shield, permission: "manage_roles_permissions" },
  { key: "reminder-schedule", label: "Reminder Schedule", icon: Bell, permission: "manage_reminder_schedule" },
  { key: "chairman-channels", label: "Chairman WhatsApp", icon: Smartphone, permission: "manage_chairman_channels" },
  { key: "deleted-items", label: "Deleted Items", icon: Trash2, permission: "view_deleted_items" },
];

const ROLE_LEVELS: Record<string, number> = {
  member: 0,
  md_admin: 0,
  general_admin: 1,
  hr_admin: 2,
  it_admin: 3,
  super_admin: 4,
  dev_admin: 5,
};

const ROLE_LABELS: Record<string, string> = {
  member: "Member",
  general_admin: "General Admin",
  md_admin: "MD Admin",
  hr_admin: "HR Admin",
  it_admin: "IT Admin",
  super_admin: "Super Admin",
  dev_admin: "Dev Admin",
  admin: "Admin",
};

export default function V2AdminPage() {
  const { containerClass } = useLayout();
  const { user, hasPermission } = useAuth();
  const visibleTabs = TABS.filter(t => hasPermission(t.permission));
  const defaultTab = visibleTabs[0]?.key ?? "kpi";
  const [activeTab, setActiveTab] = useState<AdminTab>(defaultTab);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // If the visible tabs set changes (e.g. permissions resolve after a re-fetch
  // or a custom role's grants change live), make sure the currently selected
  // tab is still permission-allowed. If not, snap back to the first visible
  // tab so a user without view_kpi_dashboard never briefly sees KPI content.
  useEffect(() => {
    if (!visibleTabs.some((t) => t.key === activeTab)) {
      setActiveTab(defaultTab);
    }
  }, [visibleTabs.map((t) => t.key).join(","), activeTab, defaultTab]);

  const versionsQuery = useQuery<{ data: GovernanceVersionRecord[] }>({
    queryKey: ["/api/governance-admin/versions"],
    enabled: hasPermission("manage_governance"),
  });

  const versions = versionsQuery.data?.data ?? [];
  const activeVersion = versions.find((v) => v.status === "active");
  const draftVersions = versions.filter((v) => v.status === "draft");

  const editingVersionId = selectedVersionId ?? draftVersions[0]?.id ?? activeVersion?.id ?? null;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #0f0c29, #1a1a3e, #24243e)" }}>
      <div className={`${containerClass} mx-auto p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-7 h-7 text-cyan-400" />
          <h1
            className="text-2xl font-bold text-white tracking-wide"
            data-testid="text-page-title"
          >
            Governance Admin
          </h1>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <Button
                key={tab.key}
                variant={isActive ? "default" : "ghost"}
                className={isActive ? "" : "text-white/60"}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`tab-${tab.key}`}
              >
                <Icon className="w-4 h-4 mr-1" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {visibleTabs.length === 0 && (
          <div
            className="rounded-lg border border-white/10 bg-white/5 p-8 text-center text-white/70"
            data-testid="text-no-admin-tabs"
          >
            <Shield className="w-8 h-8 mx-auto mb-3 text-white/40" />
            <p className="text-base font-medium text-white/80 mb-1">No admin sections available</p>
            <p className="text-sm">
              Your role grants <code>view_admin_panel</code> but no specific admin capabilities. Ask a Super Admin to grant you the relevant permissions.
            </p>
          </div>
        )}

        {visibleTabs.length > 0 && activeTab !== "versions" && activeTab !== "team-members" && activeTab !== "departments" && activeTab !== "user-accounts" && activeTab !== "deleted-items" && activeTab !== "kpi" && activeTab !== "roles-permissions" && activeTab !== "reminder-schedule" && (
          <div className="mb-4 flex items-center gap-3">
            <Label className="text-white/60 text-sm">Editing version:</Label>
            <Select
              value={editingVersionId ?? ""}
              onValueChange={(val) => setSelectedVersionId(val)}
            >
              <SelectTrigger
                className="w-[320px] bg-white/5 border-white/10 text-white"
                data-testid="select-editing-version"
              >
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} ({v.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {editingVersionId && versions.find((v) => v.id === editingVersionId)?.status !== "draft" && (
              <Badge variant="secondary" className="text-yellow-300 bg-yellow-900/30">
                Read-only (not a draft)
              </Badge>
            )}
          </div>
        )}

        {visibleTabs.length > 0 && visibleTabs.some((t) => t.key === activeTab) && (
          <>
            {activeTab === "versions" && <VersionsTab versions={versions} isLoading={versionsQuery.isLoading} />}
            {activeTab === "lanes" && editingVersionId && <LanesTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "actors" && editingVersionId && <ActorsTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "reference-lists" && editingVersionId && <ReferenceListsTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "workflow-rules" && editingVersionId && <WorkflowRulesTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "field-permissions" && editingVersionId && <FieldPermissionsTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "transition-permissions" && editingVersionId && <TransitionPermissionsTab versionId={editingVersionId} isDraft={versions.find((v) => v.id === editingVersionId)?.status === "draft"} />}
            {activeTab === "team-members" && <TeamMembersTab />}
            {activeTab === "departments" && <DepartmentsTab />}
            {activeTab === "user-accounts" && <UserAccountsTab />}
            {activeTab === "deleted-items" && <DeletedItemsTab />}
            {activeTab === "kpi" && <KpiDashboardTab />}
            {activeTab === "roles-permissions" && <RolesPermissionsTab />}
            {activeTab === "reminder-schedule" && <ReminderScheduleTab />}
            {activeTab === "chairman-channels" && <ChairmanChannelsTab />}
          </>
        )}
      </div>
    </div>
  );
}

interface AdminPermissionRecord {
  id: string;
  name: string;
  category: string;
  description: string;
}
interface AdminRoleRecord {
  id: string;
  name: string;
  label: string;
  description: string;
  level: number;
  isSystemProtected: boolean;
  isPreset: boolean;
  permissions: string[];
  userCount: number;
}
interface RoleTierRecord {
  id: string;
  level: number;
  label: string;
  isProtected: boolean;
  roleCount: number;
}

const ROLE_LEVEL_LABELS: Record<number, string> = {
  0: "Member tier",
  1: "General Admin tier",
  2: "HR Admin tier",
  3: "IT Admin tier",
  4: "Super Admin tier",
  5: "Dev Admin tier",
};

const CATEGORY_LABELS: Record<string, string> = {
  panels: "Navigation Panels",
  users: "User Accounts",
  team: "Team Members",
  governance: "Governance Configuration",
  actionItems: "Action Items",
  reports: "Reports & KPI",
  mdReview: "MD Review Workflow",
  hr: "HR Dossier",
  system: "System Administration",
};

function RolesPermissionsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [draftPerms, setDraftPerms] = useState<Set<string> | null>(null);
  const [stepUpOpen, setStepUpOpen] = useState<null | "save" | "delete" | "create" | "edit">(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleLabel, setNewRoleLabel] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [newRoleLevel, setNewRoleLevel] = useState<number>(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editLabel, setEditLabel] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editLevel, setEditLevel] = useState<number>(0);
  const [usersDialogRoleId, setUsersDialogRoleId] = useState<string | null>(null);
  const [tiersOpen, setTiersOpen] = useState(false);

  const rolesQuery = useQuery<{ data: AdminRoleRecord[] }>({
    queryKey: ["/api/admin/roles"],
  });
  const permsQuery = useQuery<{ data: AdminPermissionRecord[] }>({
    queryKey: ["/api/admin/permissions"],
  });
  const tiersQuery = useQuery<{ data: RoleTierRecord[] }>({
    queryKey: ["/api/admin/role-tiers"],
  });

  const roles = rolesQuery.data?.data ?? [];
  const permissions = permsQuery.data?.data ?? [];
  const tiers = tiersQuery.data?.data ?? [];
  // Non-protected tiers are the rungs roles may sit on; the protected tier is
  // dev_admin's pinned top rung.
  const assignableTiers = tiers.filter((t) => !t.isProtected);
  const tierLabelByLevel = useMemo(() => {
    const m: Record<number, string> = {};
    for (const t of tiers) m[t.level] = t.label;
    return m;
  }, [tiers]);
  function levelLabel(level: number): string {
    return tierLabelByLevel[level] ?? ROLE_LEVEL_LABELS[level] ?? `Level ${level}`;
  }

  const selectedRole = roles.find((r) => r.id === selectedRoleId) ?? roles[0] ?? null;
  const effectiveSelectedId = selectedRole?.id ?? null;

  const initialPerms = useMemo(
    () => new Set(selectedRole?.permissions ?? []),
    [selectedRole?.id, selectedRole?.permissions.join(",")],
  );
  const currentDraft = draftPerms ?? initialPerms;

  // The card-level state is derived from whatever permission set is in
  // `currentDraft`. Clicking a level updates `draftPerms` to the canonical
  // bundle for the new card-levels. The save mutation always sends the
  // canonical bundle — this means a role whose stored permissions don't
  // round-trip cleanly (rare; legacy hand-edits) is silently normalized
  // when the admin clicks Save, after seeing the dirty marker.
  const cardLevels = useMemo(
    () => permissionsToCardLevels(currentDraft),
    [currentDraft],
  );
  const canonicalPerms = useMemo(
    () => new Set(cardLevelsToPermissions(cardLevels)),
    [cardLevels],
  );

  const isDirty = useMemo(() => {
    if (!selectedRole) return false;
    if (canonicalPerms.size !== initialPerms.size) return true;
    let dirty = false;
    canonicalPerms.forEach((p) => { if (!initialPerms.has(p)) dirty = true; });
    return dirty;
  }, [canonicalPerms, initialPerms, selectedRole?.id]);

  function setCardLevel(cardId: string, level: PageCardLevel) {
    if (!selectedRole || selectedRole.isSystemProtected) return;
    const next = { ...cardLevels, [cardId]: level };
    setDraftPerms(new Set(cardLevelsToPermissions(next)));
  }

  const savePermsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole) throw new Error("No role selected");
      const res = await apiRequest("PUT", `/api/admin/roles/${selectedRole.id}/permissions`, {
        permissions: Array.from(canonicalPerms),
        confirmPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Role permissions updated." });
      setStepUpOpen(null);
      setConfirmPassword("");
      setDraftPerms(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignable-roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-labels"] });
    },
    onError: (err: Error) => showFromApiError("Failed to save permissions", err),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole) throw new Error("No role selected");
      const res = await apiRequest("DELETE", `/api/admin/roles/${selectedRole.id}`, {
        confirmPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Role deleted." });
      setStepUpOpen(null);
      setConfirmPassword("");
      setSelectedRoleId(null);
      setDraftPerms(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignable-roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-labels"] });
    },
    onError: (err: Error) => showFromApiError("Failed to delete role", err),
  });

  const createRoleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/roles", {
        name: newRoleName.trim(),
        label: newRoleLabel.trim(),
        description: newRoleDesc.trim(),
        level: newRoleLevel,
        permissions: [],
        confirmPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Role created." });
      setStepUpOpen(null);
      setConfirmPassword("");
      setCreateOpen(false);
      setNewRoleName("");
      setNewRoleLabel("");
      setNewRoleDesc("");
      setNewRoleLevel(0);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignable-roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-labels"] });
    },
    onError: (err: Error) => showFromApiError("Failed to create role", err),
  });

  const editRoleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRole) throw new Error("No role selected");
      const res = await apiRequest("PATCH", `/api/admin/roles/${selectedRole.id}`, {
        label: editLabel.trim(),
        description: editDesc.trim(),
        level: editLevel,
        confirmPassword,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Role updated." });
      setStepUpOpen(null);
      setConfirmPassword("");
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignable-roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-labels"] });
    },
    onError: (err: Error) => showFromApiError("Failed to update role", err),
  });

  function openEditDialog() {
    if (!selectedRole) return;
    setEditLabel(selectedRole.label);
    setEditDesc(selectedRole.description ?? "");
    setEditLevel(selectedRole.level);
    setEditOpen(true);
  }

  if (rolesQuery.isLoading || permsQuery.isLoading) {
    return <div className="text-white/60 text-sm">Loading roles &amp; permissions…</div>;
  }

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="tab-roles-permissions-content">
      <Card className="col-span-12 md:col-span-4 bg-white/5 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white text-base">Roles</CardTitle>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-white/60 hover:text-white"
              onClick={() => {
                let tz = "";
                try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
                const url = `/api/admin/role-audit-events/export${tz ? `?tz=${encodeURIComponent(tz)}` : ""}`;
                window.location.assign(url);
              }}
              data-testid="button-export-role-audit"
              title="Download all role/permission audit events as Excel"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Audit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-white/60 hover:text-white"
              onClick={() => setTiersOpen(true)}
              data-testid="button-manage-tiers"
              title="Manage the role hierarchy tiers (the ladder used by assign-role rules)"
            >
              <Layers className="w-3.5 h-3.5 mr-1" /> Tiers
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-cyan-300 border-cyan-500/30 hover:bg-cyan-500/10"
              onClick={() => setCreateOpen(true)}
              data-testid="button-create-role"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> New Role
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-1">
          {roles.map((r) => {
            const isActive = r.id === effectiveSelectedId;
            return (
              <button
                key={r.id}
                onClick={() => { setSelectedRoleId(r.id); setDraftPerms(null); }}
                className={`w-full text-left px-3 py-2 rounded transition-colors ${isActive ? "bg-cyan-500/20 border border-cyan-500/40" : "hover:bg-white/5 border border-transparent"}`}
                data-testid={`button-select-role-${r.name}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white">{r.label}</span>
                  <Badge variant="outline" className="text-xs text-white/50 border-white/20" data-testid={`badge-perm-count-${r.name}`}>{r.permissions.length} perms</Badge>
                </div>
                <div className="text-xs text-white/40 mt-0.5 font-mono">{r.name}</div>
                <div
                  className="text-xs text-white/55 mt-1 leading-snug line-clamp-2"
                  title={summarizeRolePermissions(new Set(r.permissions))}
                  data-testid={`text-role-summary-${r.name}`}
                >
                  {summarizeRolePermissions(new Set(r.permissions))}
                </div>
                <div className="flex flex-wrap gap-1 mt-1 items-center">
                  {r.isSystemProtected && (
                    <Badge variant="outline" className="text-2xs text-amber-300 border-amber-500/30">System</Badge>
                  )}
                  {r.isPreset && !r.isSystemProtected && (
                    <Badge variant="outline" className="text-2xs text-blue-300 border-blue-500/30">Preset</Badge>
                  )}
                  {!r.isPreset && (
                    <Badge variant="outline" className="text-2xs text-purple-300 border-purple-500/30">Custom</Badge>
                  )}
                  <Badge variant="outline" className="text-2xs text-cyan-300 border-cyan-500/30" data-testid={`badge-role-level-${r.name}`}>
                    L{r.level}
                  </Badge>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setUsersDialogRoleId(r.id); }}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setUsersDialogRoleId(r.id); } }}
                    className="ml-auto inline-flex items-center rounded-md border border-emerald-500/30 px-1.5 py-0.5 text-2xs text-emerald-300 hover:bg-emerald-500/10 cursor-pointer"
                    data-testid={`badge-user-count-${r.name}`}
                  >
                    {r.userCount} {r.userCount === 1 ? "user" : "users"}
                  </span>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <Card className="col-span-12 md:col-span-8 bg-white/5 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white text-base">
              {selectedRole?.label ?? "Select a role"}
            </CardTitle>
            {selectedRole?.description && (
              <p className="text-xs text-white/50 mt-1">{selectedRole.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            {selectedRole && !selectedRole.isSystemProtected && (
              <Button
                size="sm"
                variant="outline"
                className="text-white/70 border-white/20 hover:bg-white/10"
                onClick={openEditDialog}
                data-testid="button-edit-role"
              >
                Edit Label
              </Button>
            )}
            {selectedRole && !selectedRole.isPreset && !selectedRole.isSystemProtected && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-300 border-red-500/30 hover:bg-red-500/10"
                onClick={() => setStepUpOpen("delete")}
                data-testid="button-delete-role"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
            {selectedRole && !selectedRole.isSystemProtected && (
              <Button
                size="sm"
                disabled={!isDirty}
                onClick={() => setStepUpOpen("save")}
                data-testid="button-save-permissions"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                <Save className="w-3.5 h-3.5 mr-1" />
                Save Changes{isDirty ? "" : " (no changes)"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!selectedRole && <div className="text-white/50 text-sm">Pick a role on the left to inspect or edit its permissions.</div>}
          {selectedRole && selectedRole.isSystemProtected && (
            <div className="mb-4 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs">
              This role is system-protected. Its permissions cannot be edited from this UI to keep the platform safe (e.g. dev_admin always has every permission, super_admin always retains <code>manage_roles_permissions</code> so the matrix cannot be locked out).
            </div>
          )}
          {selectedRole && (
            <div className="space-y-6">
              {(["app", "admin", "settings"] as PageCardSection[]).map((section) => {
                const cards = PAGE_CARDS.filter((c) => c.section === section);
                if (cards.length === 0) return null;
                return (
                  <div key={section} data-testid={`role-section-${section}`}>
                    <div className="mb-2">
                      <div className="text-sm uppercase tracking-wider text-white/60 font-semibold">
                        {SECTION_LABELS[section]}
                      </div>
                      <div className="text-xs text-white/40 mt-0.5">{SECTION_NOTES[section]}</div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {cards.map((card) => {
                        const current = cardLevels[card.id] ?? "none";
                        const disabled = selectedRole.isSystemProtected;
                        return (
                          <div
                            key={card.id}
                            className={`p-3 rounded border bg-white/5 border-white/10 ${disabled ? "opacity-70" : ""}`}
                            data-testid={`card-page-${card.id}`}
                          >
                            <div className="text-xs font-medium text-white">{card.label}</div>
                            <div className="text-xs text-white/50 mt-0.5 leading-snug">{card.description}</div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {card.availableLevels.map((lvl) => {
                                const active = current === lvl;
                                const defaultLabelMap: Record<PageCardLevel, string> = {
                                  none: "None",
                                  view: "View",
                                  edit: "Edit",
                                  full: "Full Authority",
                                };
                                const labelMap: Record<PageCardLevel, string> = {
                                  ...defaultLabelMap,
                                  ...(card.levelLabels ?? {}),
                                };
                                return (
                                  <button
                                    key={lvl}
                                    type="button"
                                    disabled={disabled}
                                    onClick={() => setCardLevel(card.id, lvl)}
                                    className={`text-xs px-2 py-1 rounded border transition-colors ${
                                      active
                                        ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-200"
                                        : "bg-transparent border-white/15 text-white/55 hover:bg-white/10 hover:text-white/80"
                                    } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                                    data-testid={`button-level-${card.id}-${lvl}`}
                                  >
                                    {labelMap[lvl]}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="pt-3 border-t border-white/10">
                <div className="text-sm uppercase tracking-wider text-white/60 font-semibold mb-1">
                  Always visible to all signed-in users
                </div>
                <div className="text-xs text-white/40 mb-2 leading-snug">
                  These pages aren't permission-gated and are available to every account regardless of role. They're listed here so you know where they live.
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ALWAYS_VISIBLE_DESTINATIONS.map((d) => (
                    <span
                      key={d.label}
                      title={d.description}
                      className="text-xs px-2 py-1 rounded border border-white/10 bg-white/5 text-white/60"
                      data-testid={`pill-always-visible-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {d.label}
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-white/45 leading-snug">
                  <strong className="text-white/70">Identity bindings:</strong> users bound to <em>SDF MD</em>, <em>SDF PMO</em>, or <em>SDF Board Secretary</em> team-member records keep their MD-review and dispatch capabilities regardless of this matrix. Status transitions are governed separately by the <strong>Transition Permissions</strong> tab.
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) { setCreateOpen(false); setStepUpOpen(null); setConfirmPassword(""); } }}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Create Custom Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Internal Name (lowercase, underscores)</Label>
              <Input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. compliance_officer"
                className="bg-white/5 border-white/10 text-white font-mono text-xs"
                data-testid="input-new-role-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Display Label</Label>
              <Input
                value={newRoleLabel}
                onChange={(e) => setNewRoleLabel(e.target.value)}
                placeholder="Compliance Officer"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-role-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Description (optional)</Label>
              <Textarea
                value={newRoleDesc}
                onChange={(e) => setNewRoleDesc(e.target.value)}
                rows={3}
                className="bg-white/5 border-white/10 text-white text-xs"
                data-testid="input-new-role-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Hierarchy Tier</Label>
              <select
                value={newRoleLevel}
                onChange={(e) => setNewRoleLevel(Number(e.target.value))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded px-2 py-1.5"
                data-testid="select-new-role-level"
              >
                {assignableTiers.map((t) => (
                  <option key={t.id} value={t.level} className="bg-[#0d1b3e] text-white">
                    L{t.level} — {t.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-white/40">
                Determines who can assign this role and which target users it can act on. Tiers are managed from the <strong>Tiers</strong> button; only the protected Dev Admin tier sits above the ones listed here.
              </p>
            </div>
            {stepUpOpen === "create" && (
              <div className="space-y-1.5 pt-2 border-t border-white/10">
                <Label className="text-white/60 text-xs">Confirm with your password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="input-create-step-up-password"
                  autoComplete="current-password"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCreateOpen(false); setStepUpOpen(null); setConfirmPassword(""); setNewRoleLevel(0); }} className="text-white/60">Cancel</Button>
            {stepUpOpen !== "create" ? (
              <Button
                disabled={!newRoleName.trim() || !newRoleLabel.trim()}
                onClick={() => setStepUpOpen("create")}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="button-create-role-continue"
              >Continue</Button>
            ) : (
              <Button
                disabled={!confirmPassword || createRoleMutation.isPending}
                onClick={() => createRoleMutation.mutate()}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="button-create-role-confirm"
              >
                {createRoleMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Role"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stepUpOpen === "save" || stepUpOpen === "delete"} onOpenChange={(o) => { if (!o) { setStepUpOpen(null); setConfirmPassword(""); } }}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">
              {stepUpOpen === "delete" ? "Delete Role" : "Confirm Permission Changes"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-white/60">
              {stepUpOpen === "delete"
                ? <>You are about to permanently delete the role <strong>{selectedRole?.label}</strong>. This cannot be undone.</>
                : <>You are about to update the permissions for <strong>{selectedRole?.label}</strong>. Confirm with <strong>your own</strong> password.</>}
            </p>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Your Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-step-up-password"
                autoComplete="current-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setStepUpOpen(null); setConfirmPassword(""); }} className="text-white/60">Cancel</Button>
            {stepUpOpen === "delete" ? (
              <Button
                disabled={!confirmPassword || deleteRoleMutation.isPending}
                onClick={() => deleteRoleMutation.mutate()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="button-confirm-delete-role"
              >
                {deleteRoleMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete Role"}
              </Button>
            ) : (
              <Button
                disabled={!confirmPassword || savePermsMutation.isPending}
                onClick={() => savePermsMutation.mutate()}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="button-confirm-save-permissions"
              >
                {savePermsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={(o) => { if (!o) { setEditOpen(false); setStepUpOpen(null); setConfirmPassword(""); } }}>
        <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-white/60">
              Update the display label, description, and hierarchy tier for <strong>{selectedRole?.name}</strong>. The internal name cannot be changed.
            </p>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Display Label</Label>
              <Input
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-edit-role-label"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Description</Label>
              <Textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                rows={3}
                className="bg-white/5 border-white/10 text-white text-xs"
                data-testid="input-edit-role-description"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Hierarchy Tier</Label>
              <select
                value={editLevel}
                onChange={(e) => setEditLevel(Number(e.target.value))}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded px-2 py-1.5"
                data-testid="select-edit-role-level"
              >
                {assignableTiers.map((t) => (
                  <option key={t.id} value={t.level} className="bg-[#0d1b3e] text-white">
                    L{t.level} — {t.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-white/40">
                Moving a role changes who can assign it and which users it can act on. The protected Dev Admin tier cannot be chosen.
              </p>
            </div>
            {stepUpOpen === "edit" && (
              <div className="space-y-1.5 pt-2 border-t border-white/10">
                <Label className="text-white/60 text-xs">Confirm with your password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="input-edit-step-up-password"
                  autoComplete="current-password"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditOpen(false); setStepUpOpen(null); setConfirmPassword(""); }} className="text-white/60">Cancel</Button>
            {stepUpOpen !== "edit" ? (
              <Button
                disabled={!editLabel.trim()}
                onClick={() => setStepUpOpen("edit")}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="button-edit-role-continue"
              >Continue</Button>
            ) : (
              <Button
                disabled={!confirmPassword || editRoleMutation.isPending}
                onClick={() => editRoleMutation.mutate()}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="button-edit-role-confirm"
              >
                {editRoleMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Role"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RoleUsersDialog
        roleId={usersDialogRoleId}
        roleLabel={roles.find((r) => r.id === usersDialogRoleId)?.label ?? ""}
        onClose={() => setUsersDialogRoleId(null)}
      />

      <RoleTiersDialog
        open={tiersOpen}
        tiers={tiers}
        onClose={() => setTiersOpen(false)}
      />
    </div>
  );
}

interface TierDraft {
  id: string;
  level: number;
  label: string;
  isProtected: boolean;
  roleCount: number;
}

function RoleTiersDialog({
  open,
  tiers,
  onClose,
}: {
  open: boolean;
  tiers: RoleTierRecord[];
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [draft, setDraft] = useState<TierDraft[]>([]);
  const [stepUp, setStepUp] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    if (open) {
      // Display highest authority at the top. The protected tier always sits
      // at the very top; non-protected tiers descend below it.
      setDraft(
        [...tiers]
          .sort((a, b) => b.level - a.level)
          .map((t) => ({ ...t })),
      );
      setStepUp(false);
      setConfirmPassword("");
    }
  }, [open, tiers]);

  const protectedCount = draft.filter((t) => t.isProtected).length;
  const firstMovableIdx = draft.findIndex((t) => !t.isProtected);
  const lastMovableIdx = (() => {
    let last = -1;
    draft.forEach((t, i) => { if (!t.isProtected) last = i; });
    return last;
  })();

  function move(index: number, dir: -1 | 1) {
    setDraft((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      if (next[index].isProtected || next[target].isProtected) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function rename(index: number, label: string) {
    setDraft((prev) => prev.map((t, i) => (i === index ? { ...t, label } : t)));
  }

  function addTier() {
    // New tiers join at the bottom (lowest authority).
    setDraft((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        level: -1,
        label: "New tier",
        isProtected: false,
        roleCount: 0,
      },
    ]);
  }

  function removeTier(index: number) {
    setDraft((prev) => prev.filter((_, i) => i !== index));
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Backend wants non-protected tiers ordered LOWEST→HIGHEST. The draft is
      // displayed HIGHEST→LOWEST, so drop the protected tier and reverse.
      const ascending = draft.filter((t) => !t.isProtected).reverse();
      const payload = {
        tiers: ascending.map((t) => ({
          id: t.id.startsWith("new-") ? null : t.id,
          label: t.label.trim(),
        })),
        confirmPassword,
      };
      const res = await apiRequest("PUT", "/api/admin/role-tiers", payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Tiers updated." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-tiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignable-roles"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/role-labels"] });
      onClose();
    },
    onError: (err: Error) => showFromApiError("Failed to update tiers", err),
  });

  const hasEmptyLabel = draft.some((t) => !t.label.trim());
  const hasDeletedInUse = tiers.some(
    (orig) =>
      !orig.isProtected &&
      orig.roleCount > 0 &&
      !draft.some((d) => d.id === orig.id),
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setStepUp(false); setConfirmPassword(""); } }}>
      <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Role Hierarchy Tiers</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <p className="text-xs text-white/60">
            Tiers form the ladder used to decide who can assign which role and act on which users. Higher tiers sit above lower ones. The protected <strong>Dev Admin</strong> tier is always pinned at the very top and cannot be renamed, moved, or removed.
          </p>
          <div className="space-y-1.5">
            {draft.map((t, idx) => {
              return (
                <div
                  key={t.id}
                  className="flex items-center gap-2 bg-white/5 border border-white/10 rounded px-2 py-1.5"
                  data-testid={`row-tier-${t.id}`}
                >
                  <div className="flex flex-col">
                    <button
                      type="button"
                      disabled={t.isProtected || idx === firstMovableIdx}
                      onClick={() => move(idx, -1)}
                      className="text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                      data-testid={`button-tier-up-${t.id}`}
                      title="Move up (higher authority)"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={t.isProtected || idx === lastMovableIdx}
                      onClick={() => move(idx, 1)}
                      className="text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                      data-testid={`button-tier-down-${t.id}`}
                      title="Move down (lower authority)"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input
                    value={t.label}
                    disabled={t.isProtected}
                    onChange={(e) => rename(idx, e.target.value)}
                    className="bg-white/5 border-white/10 text-white text-sm flex-1 disabled:opacity-60"
                    data-testid={`input-tier-label-${t.id}`}
                  />
                  <span className="text-xs text-white/40 w-16 text-right" data-testid={`text-tier-rolecount-${t.id}`}>
                    {t.roleCount} role{t.roleCount === 1 ? "" : "s"}
                  </span>
                  {t.isProtected ? (
                    <span className="text-xs text-cyan-300/80 w-7 text-center" title="Protected — cannot be removed or moved">
                      🔒
                    </span>
                  ) : (
                    <button
                      type="button"
                      disabled={t.roleCount > 0}
                      onClick={() => removeTier(idx)}
                      className="text-rose-300/70 hover:text-rose-300 disabled:opacity-20 disabled:cursor-not-allowed w-7 flex justify-center"
                      data-testid={`button-tier-delete-${t.id}`}
                      title={t.roleCount > 0 ? "In use — reassign its roles first" : "Delete tier"}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={addTier}
            className="text-cyan-300 hover:bg-cyan-500/10"
            data-testid="button-add-tier"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Tier
          </Button>
          {hasDeletedInUse && (
            <p className="text-xs text-rose-300/80">
              You removed a tier that still has roles assigned. Reassign those roles to another tier first.
            </p>
          )}
          {stepUp && (
            <div className="space-y-1.5 pt-2 border-t border-white/10">
              <Label className="text-white/60 text-xs">Confirm with your password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-tiers-step-up-password"
                autoComplete="current-password"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { onClose(); setStepUp(false); setConfirmPassword(""); }} className="text-white/60">Cancel</Button>
          {!stepUp ? (
            <Button
              disabled={hasEmptyLabel || hasDeletedInUse || protectedCount !== 1}
              onClick={() => setStepUp(true)}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="button-tiers-continue"
            >Continue</Button>
          ) : (
            <Button
              disabled={!confirmPassword || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="button-tiers-confirm"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Tiers"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RoleUserRecord {
  id: string;
  username: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean | null;
}

function RoleUsersDialog({ roleId, roleLabel, onClose }: { roleId: string | null; roleLabel: string; onClose: () => void }) {
  const query = useQuery<{ data: RoleUserRecord[] }>({
    queryKey: ["/api/admin/roles", roleId, "users"],
    enabled: !!roleId,
  });
  const list = query.data?.data ?? [];
  return (
    <Dialog open={!!roleId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0d1b3e] border-white/10 max-w-lg" data-testid="dialog-role-users">
        <DialogHeader>
          <DialogTitle className="text-white">Users with role: {roleLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 pt-2 max-h-[60vh] overflow-y-auto">
          {query.isLoading && <div className="text-white/50 text-sm">Loading…</div>}
          {!query.isLoading && list.length === 0 && (
            <div className="text-white/50 text-sm" data-testid="text-role-users-empty">No users currently hold this role.</div>
          )}
          {list.map((u) => {
            const fullName = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
            return (
              <div
                key={u.id}
                className="flex items-center justify-between rounded border border-white/10 bg-white/5 px-3 py-2"
                data-testid={`row-role-user-${u.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{fullName || u.username || u.email || u.id}</div>
                  <div className="text-sm text-white/50 truncate font-mono">{u.username ?? u.email ?? ""}</div>
                </div>
                <Badge
                  variant="outline"
                  className={`text-xs ${u.isActive ? "text-emerald-300 border-emerald-500/30" : "text-white/40 border-white/20"}`}
                  data-testid={`badge-role-user-active-${u.id}`}
                >
                  {u.isActive ? "Active" : "Disabled"}
                </Badge>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-white/60" data-testid="button-close-role-users">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VersionsTab({ versions, isLoading }: { versions: GovernanceVersionRecord[]; isLoading: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneDate, setCloneDate] = useState("");
  const [activateConfirmId, setActivateConfirmId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const cloneMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/governance-admin/versions/clone-active", {
        name: cloneName,
        effectiveFrom: cloneDate,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions"] });
      setShowCloneDialog(false);
      setCloneName("");
      setCloneDate("");
      toast({ title: "Draft version created", description: "Cloned from active version successfully." });
    },
    onError: (err: any) => {
      showFromApiError("Failed to create draft", err);
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (versionId: string) => {
      const res = await apiRequest("POST", `/api/governance-admin/versions/${versionId}/activate`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/governance"] });
      toast({ title: "Version activated", description: "Previous active version retired." });
    },
    onError: (err: any) => {
      showFromApiError("Failed to activate", err);
    },
  });

  const deleteDraftMutation = useMutation({
    mutationFn: async (versionId: string) => {
      const res = await apiRequest("DELETE", `/api/governance-admin/versions/${versionId}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions"] });
      setDeleteConfirmId(null);
      toast({ title: "Draft deleted", description: "The draft version has been removed." });
    },
    onError: (err: any) => {
      showFromApiError("Failed to delete draft", err);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Governance Versions</h2>
        <Button onClick={() => setShowCloneDialog(true)} data-testid="button-create-draft">
          <Copy className="w-4 h-4 mr-1" />
          Create Draft Version
        </Button>
      </div>

      <div className="space-y-3">
        {versions.map((v) => (
          <Card key={v.id} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-medium" data-testid={`text-version-name-${v.id}`}>
                    {v.name}
                  </span>
                  <Badge
                    variant={v.status === "active" ? "default" : v.status === "draft" ? "secondary" : "outline"}
                    className={
                      v.status === "active"
                        ? "bg-green-600/80 text-white"
                        : v.status === "draft"
                        ? "bg-blue-600/50 text-blue-200"
                        : "text-white/40"
                    }
                    data-testid={`badge-status-${v.id}`}
                  >
                    {v.status.toUpperCase()}
                  </Badge>
                  <span className="text-white/40 text-sm">
                    Effective: {new Date(v.effective_from).toLocaleDateString()}
                  </span>
                  <span className="text-white/30 text-xs font-mono">
                    {v.id.substring(0, 8)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {v.status === "draft" && (
                    <>
                      <Button
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        onClick={() => setDeleteConfirmId(v.id)}
                        disabled={deleteDraftMutation.isPending}
                        data-testid={`button-delete-draft-${v.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete Draft
                      </Button>
                      <Button
                        variant="default"
                        onClick={() => setActivateConfirmId(v.id)}
                        disabled={activateMutation.isPending}
                        data-testid={`button-activate-${v.id}`}
                      >
                        <Zap className="w-4 h-4 mr-1" />
                        Activate
                      </Button>
                    </>
                  )}
                  {v.status === "retired" && (
                    <Archive className="w-4 h-4 text-white/20" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Create Draft Version</DialogTitle>
          </DialogHeader>
          <p className="text-white/50 text-sm leading-relaxed">
            A draft is a working copy of the current governance configuration. After creating it, switch to the other tabs (Reference Lists, Lanes, etc.) to make your changes. When you have finished amending, come back here to the Versions tab and you will see a teal colored bubble saying "Activate" — click it to activate your new Version.
          </p>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Version Name</Label>
              <Input
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder="e.g. Governance Framework v2.0"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-clone-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Effective From</Label>
              <Input
                type="date"
                value={cloneDate}
                onChange={(e) => setCloneDate(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-clone-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowCloneDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => cloneMutation.mutate()}
              disabled={!cloneName || !cloneDate || cloneMutation.isPending}
              data-testid="button-confirm-clone"
            >
              {cloneMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Copy className="w-4 h-4 mr-1" />}
              Clone & Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!activateConfirmId} onOpenChange={(open) => { if (!open) setActivateConfirmId(null); }}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Activate This Version?</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm leading-relaxed">
            Have you finished all your changes? Once activated, this version becomes the live governance configuration and can no longer be edited. The current active version will be retired.
          </p>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setActivateConfirmId(null)} data-testid="button-activate-cancel">
              Go Back
            </Button>
            <Button
              onClick={() => {
                if (activateConfirmId) {
                  activateMutation.mutate(activateConfirmId);
                  setActivateConfirmId(null);
                }
              }}
              disabled={activateMutation.isPending}
              data-testid="button-activate-confirm"
            >
              {activateMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
              Yes, Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Delete Draft Version?</DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm leading-relaxed">
            This will permanently remove the draft version and all its configuration (lanes, actors, reference lists, workflow rules, and field permissions). This cannot be undone.
          </p>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
            <p className="text-red-300 text-sm font-medium">
              {versions.find((v) => v.id === deleteConfirmId)?.name}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setDeleteConfirmId(null)} data-testid="button-delete-draft-cancel">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirmId) {
                  deleteDraftMutation.mutate(deleteConfirmId);
                }
              }}
              disabled={deleteDraftMutation.isPending}
              data-testid="button-delete-draft-confirm"
            >
              {deleteDraftMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Yes, Delete Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LanesTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const lanesQuery = useQuery<{ data: AuthorityLaneRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "lanes"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/lanes`);
      return res.json();
    },
  });

  const lanes = lanesQuery.data?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/governance-admin/lanes/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "lanes"] });
      toast({ title: "Lane updated" });
    },
    onError: (err: any) => {
      showFromApiError("Update failed", err);
    },
  });

  if (lanesQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">Authority Lanes</h2>
      <div className="space-y-3">
        {lanes.map((lane) => (
          <LaneRow key={lane.id} lane={lane} isDraft={isDraft} onSave={(data) => updateMutation.mutate({ id: lane.id, data })} />
        ))}
      </div>
    </div>
  );
}

function LaneRow({ lane, isDraft, onSave }: { lane: AuthorityLaneRecord; isDraft: boolean; onSave: (data: Record<string, unknown>) => void }) {
  const [displayName, setDisplayName] = useState(lane.display_name);
  const [sortOrder, setSortOrder] = useState(lane.sort_order);
  const [isActive, setIsActive] = useState(lane.is_active);
  const changed = displayName !== lane.display_name || sortOrder !== lane.sort_order || isActive !== lane.is_active;

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Badge variant="outline" className="font-mono text-cyan-300 border-cyan-800/50">{lane.lane_key}</Badge>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={!isDraft}
            className="w-48 bg-white/5 border-white/10 text-white"
            data-testid={`input-lane-name-${lane.id}`}
          />
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Sort:</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              disabled={!isDraft}
              className="w-20 bg-white/5 border-white/10 text-white"
              data-testid={`input-lane-sort-${lane.id}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Active:</Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!isDraft} data-testid={`switch-lane-active-${lane.id}`} />
          </div>
          {isDraft && changed && (
            <Button
              variant="default"
              onClick={() => onSave({ displayName, sortOrder, isActive })}
              data-testid={`button-save-lane-${lane.id}`}
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ActorsTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const actorsQuery = useQuery<{ data: AuthorityActorRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "actors"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/actors`);
      return res.json();
    },
  });

  const actors = actorsQuery.data?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/governance-admin/actors/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "actors"] });
      toast({ title: "Actor updated" });
    },
    onError: (err: any) => {
      showFromApiError("Update failed", err);
    },
  });

  if (actorsQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">Authority Actors</h2>
      <div className="space-y-3">
        {actors.map((actor) => (
          <ActorRow key={actor.id} actor={actor} isDraft={isDraft} onSave={(data) => updateMutation.mutate({ id: actor.id, data })} />
        ))}
      </div>
    </div>
  );
}

function ActorRow({ actor, isDraft, onSave }: { actor: AuthorityActorRecord; isDraft: boolean; onSave: (data: Record<string, unknown>) => void }) {
  const [displayName, setDisplayName] = useState(actor.display_name);
  const [laneKey, setLaneKey] = useState<string>(actor.lane_key);
  const [canReview, setCanReview] = useState(actor.can_review);
  const [canSignoff, setCanSignoff] = useState(actor.can_signoff);
  const [isActive, setIsActive] = useState(actor.is_active);
  const changed = displayName !== actor.display_name || laneKey !== actor.lane_key || canReview !== actor.can_review || canSignoff !== actor.can_signoff || isActive !== actor.is_active;

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Badge variant="outline" className="font-mono text-purple-300 border-purple-800/50">{actor.actor_key}</Badge>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={!isDraft}
            className="w-44 bg-white/5 border-white/10 text-white"
            data-testid={`input-actor-name-${actor.id}`}
          />
          <Select value={laneKey} onValueChange={setLaneKey} disabled={!isDraft}>
            <SelectTrigger className="w-44 bg-white/5 border-white/10 text-white" data-testid={`select-actor-lane-${actor.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {V2_LANE_KEYS.map((k) => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Review:</Label>
            <Switch checked={canReview} onCheckedChange={setCanReview} disabled={!isDraft} data-testid={`switch-actor-review-${actor.id}`} />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Signoff:</Label>
            <Switch checked={canSignoff} onCheckedChange={setCanSignoff} disabled={!isDraft} data-testid={`switch-actor-signoff-${actor.id}`} />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Active:</Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!isDraft} data-testid={`switch-actor-active-${actor.id}`} />
          </div>
          {isDraft && changed && (
            <Button
              variant="default"
              onClick={() => onSave({ displayName, laneKey, canReview, canSignoff, isActive })}
              data-testid={`button-save-actor-${actor.id}`}
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ReferenceListsTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [selectedListKey, setSelectedListKey] = useState<string>("PRIORITY");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newItemKey, setNewItemKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newSortOrder, setNewSortOrder] = useState(0);
  const [newRefCode, setNewRefCode] = useState("");

  const refsQuery = useQuery<{ data: ReferenceListItemRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "reference-lists"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/reference-lists`);
      return res.json();
    },
  });

  const allItems = refsQuery.data?.data ?? [];
  const listKeys = useMemo(() => {
    return [...V2_REFERENCE_LIST_KEYS].sort();
  }, []);

  const filteredItems = allItems.filter((i) => i.list_key === selectedListKey).sort((a, b) => a.sort_order - b.sort_order);

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/governance-admin/reference-lists/item/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "reference-lists"] });
      toast({ title: "Item updated" });
    },
    onError: (err: any) => {
      showFromApiError("Update failed", err);
    },
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/governance-admin/reference-lists/item", {
        governanceVersionId: versionId,
        listKey: selectedListKey,
        itemKey: newItemKey,
        label: newLabel,
        sortOrder: newSortOrder,
        ...(selectedListKey === "MEETING_TYPE" && newRefCode ? { refCode: newRefCode } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "reference-lists"] });
      setShowAddDialog(false);
      setNewItemKey("");
      setNewLabel("");
      setNewSortOrder(0);
      setNewRefCode("");
      toast({ title: "Item added" });
    },
    onError: (err: any) => {
      showFromApiError("Add failed", err);
    },
  });

  if (refsQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white">Reference Lists</h2>
        <div className="flex items-center gap-3">
          <Select value={selectedListKey} onValueChange={setSelectedListKey}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="select-list-key">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {listKeys.map((k) => (
                <SelectItem key={k} value={k}>{k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isDraft && (
            <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-ref-item">
              <Plus className="w-4 h-4 mr-1" />
              Add Item
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {filteredItems.map((item) => (
          <RefListItemRow key={item.id} item={item} isDraft={isDraft} onSave={(data) => updateMutation.mutate({ id: item.id, data })} />
        ))}
        {filteredItems.length === 0 && (
          <div className="text-white/40 text-center py-8">No items in this list</div>
        )}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add Item to {selectedListKey}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Item Key (immutable)</Label>
              <Input
                value={newItemKey}
                onChange={(e) => setNewItemKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                placeholder="e.g. NEW_ITEM"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-ref-item-key"
              />
            </div>
            <div>
              <Label className="text-white/80">Label</Label>
              <Input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Display label"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-ref-label"
              />
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(Number(e.target.value))}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-ref-sort"
              />
            </div>
            {selectedListKey === "MEETING_TYPE" && (
              <div>
                <Label className="text-white/80">Ref Code (for auto-generated references)</Label>
                <Input
                  value={newRefCode}
                  onChange={(e) => setNewRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                  placeholder="e.g. SDFMD"
                  className="bg-white/5 border-white/10 text-white font-mono"
                  data-testid="input-new-ref-code"
                />
                <p className="text-white/40 text-xs mt-1">Used in auto-generated reference numbers (e.g. 041626/SDFMD/MTG/001)</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={!newItemKey || !newLabel || addMutation.isPending}
              data-testid="button-confirm-add-ref"
            >
              {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RefListItemRow({ item, isDraft, onSave }: { item: ReferenceListItemRecord; isDraft: boolean; onSave: (data: Record<string, unknown>) => void }) {
  const [label, setLabel] = useState(item.label);
  const [sortOrder, setSortOrder] = useState(item.sort_order);
  const [isActive, setIsActive] = useState(item.is_active);
  const [refCode, setRefCode] = useState(item.ref_code || "");
  const isMeetingType = item.list_key === "MEETING_TYPE";
  const changed = label !== item.label || sortOrder !== item.sort_order || isActive !== item.is_active || (isMeetingType && refCode !== (item.ref_code || ""));

  return (
    <Card className={`border-white/10 ${item.is_active ? "bg-white/5" : "bg-white/2 opacity-50"}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-4 flex-wrap">
          <Badge variant="outline" className="font-mono text-orange-300 border-orange-800/50">{item.item_key}</Badge>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={!isDraft}
            className="w-48 bg-white/5 border-white/10 text-white"
            data-testid={`input-ref-label-${item.id}`}
          />
          {isMeetingType && (
            <div className="flex items-center gap-2">
              <Label className="text-white/60 text-sm">Ref Code:</Label>
              <Input
                value={refCode}
                onChange={(e) => setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                disabled={!isDraft}
                placeholder="e.g. SDFMD"
                className="w-28 bg-white/5 border-white/10 text-white font-mono text-xs"
                data-testid={`input-ref-code-${item.id}`}
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Sort:</Label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              disabled={!isDraft}
              className="w-20 bg-white/5 border-white/10 text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-white/60 text-sm">Active:</Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} disabled={!isDraft} data-testid={`switch-ref-active-${item.id}`} />
          </div>
          {isDraft && changed && (
            <Button
              variant="default"
              onClick={() => onSave({ label, sortOrder, isActive, ...(isMeetingType ? { refCode: refCode || null } : {}) })}
              data-testid={`button-save-ref-${item.id}`}
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowRulesTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRuleKey, setNewRuleKey] = useState("");
  const [newScopeLane, setNewScopeLane] = useState("");
  const [newScopeOrigin, setNewScopeOrigin] = useState("");
  const [newValueJson, setNewValueJson] = useState("{}");

  const rulesQuery = useQuery<{ data: WorkflowRuleRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "workflow-rules"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/workflow-rules`);
      return res.json();
    },
  });

  const rules = rulesQuery.data?.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await apiRequest("PATCH", `/api/governance-admin/workflow-rules/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "workflow-rules"] });
      toast({ title: "Rule updated" });
    },
    onError: (err: any) => {
      showFromApiError("Update failed", err);
    },
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/governance-admin/workflow-rules", {
        governanceVersionId: versionId,
        ruleKey: newRuleKey,
        scopeLaneKey: newScopeLane && newScopeLane !== "__none__" ? newScopeLane : null,
        scopeOriginType: newScopeOrigin || null,
        valueJson: newValueJson,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "workflow-rules"] });
      setShowAddDialog(false);
      setNewRuleKey("");
      setNewScopeLane("");
      setNewScopeOrigin("");
      setNewValueJson("{}");
      toast({ title: "Rule added" });
    },
    onError: (err: any) => {
      showFromApiError("Add failed", err);
    },
  });

  if (rulesQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white">Workflow Rules</h2>
        {isDraft && (
          <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-rule">
            <Plus className="w-4 h-4 mr-1" />
            Add Rule
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {rules.map((rule) => (
          <WorkflowRuleRow key={rule.id} rule={rule} isDraft={isDraft} onSave={(data) => updateMutation.mutate({ id: rule.id, data })} />
        ))}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add Workflow Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Rule Key</Label>
              <Input value={newRuleKey} onChange={(e) => setNewRuleKey(e.target.value)} placeholder="e.g. REVIEW_REQUIRED" className="bg-white/5 border-white/10 text-white" data-testid="input-new-rule-key" />
            </div>
            <div>
              <Label className="text-white/80">Scope Lane (optional)</Label>
              <Select value={newScopeLane} onValueChange={setNewScopeLane}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue placeholder="Any lane" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {V2_LANE_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80">Scope Meeting Type (optional)</Label>
              <Input value={newScopeOrigin} onChange={(e) => setNewScopeOrigin(e.target.value)} placeholder="e.g. CHAIRMAN" className="bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-white/80">Value JSON</Label>
              <Textarea value={newValueJson} onChange={(e) => setNewValueJson(e.target.value)} className="font-mono bg-white/5 border-white/10 text-white" data-testid="input-new-rule-value" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={!newRuleKey || addMutation.isPending}
              data-testid="button-confirm-add-rule"
            >
              {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WorkflowRuleRow({ rule, isDraft, onSave }: { rule: WorkflowRuleRecord; isDraft: boolean; onSave: (data: Record<string, unknown>) => void }) {
  const [ruleKey, setRuleKey] = useState(rule.rule_key);
  const [valueJson, setValueJson] = useState(typeof rule.value_json === "string" ? rule.value_json : JSON.stringify(rule.value_json, null, 2));
  const changed = ruleKey !== rule.rule_key || valueJson !== (typeof rule.value_json === "string" ? rule.value_json : JSON.stringify(rule.value_json, null, 2));

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-3">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <Input
              value={ruleKey}
              onChange={(e) => setRuleKey(e.target.value)}
              disabled={!isDraft}
              className="w-48 bg-white/5 border-white/10 text-white font-mono text-sm"
              data-testid={`input-rule-key-${rule.id}`}
            />
            <div className="flex gap-2">
              {rule.scope_lane_key && <Badge variant="outline" className="text-cyan-300 border-cyan-800/50 text-xs">{rule.scope_lane_key}</Badge>}
              {!rule.scope_lane_key && <span className="text-white/30 text-xs">Global scope</span>}
            </div>
          </div>
          <Textarea
            value={valueJson}
            onChange={(e) => setValueJson(e.target.value)}
            disabled={!isDraft}
            className="flex-1 min-w-[200px] font-mono text-sm bg-white/5 border-white/10 text-white"
            rows={2}
            data-testid={`input-rule-value-${rule.id}`}
          />
          {isDraft && changed && (
            <Button
              variant="default"
              onClick={() => onSave({ ruleKey, valueJson })}
              data-testid={`button-save-rule-${rule.id}`}
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FieldPermissionsTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState("");
  const [newRoleKey, setNewRoleKey] = useState("");
  const [newPhaseKey, setNewPhaseKey] = useState("");
  const [newCanEdit, setNewCanEdit] = useState(false);

  const permsQuery = useQuery<{ data: FieldPermissionRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "field-permissions"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/field-permissions`);
      return res.json();
    },
  });

  const perms = permsQuery.data?.data ?? [];

  const groupedByField = useMemo(() => {
    const map = new Map<string, FieldPermissionRecord[]>();
    for (const p of perms) {
      if (!map.has(p.field_key)) map.set(p.field_key, []);
      map.get(p.field_key)!.push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [perms]);

  const toggleMutation = useMutation({
    mutationFn: async ({ id, canEdit }: { id: string; canEdit: boolean }) => {
      const res = await apiRequest("PATCH", `/api/governance-admin/field-permissions/${id}`, { canEdit });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "field-permissions"] });
    },
    onError: (err: any) => {
      showFromApiError("Update failed", err);
    },
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/governance-admin/field-permissions", {
        governanceVersionId: versionId,
        fieldKey: newFieldKey,
        roleKey: newRoleKey,
        phaseKey: newPhaseKey,
        canEdit: newCanEdit,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "field-permissions"] });
      setShowAddDialog(false);
      setNewFieldKey("");
      setNewRoleKey("");
      setNewPhaseKey("");
      setNewCanEdit(false);
      toast({ title: "Permission added" });
    },
    onError: (err: any) => {
      showFromApiError("Add failed", err);
    },
  });

  if (permsQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white">Field Permissions</h2>
        {isDraft && (
          <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-permission">
            <Plus className="w-4 h-4 mr-1" />
            Add Permission
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {groupedByField.map(([fieldKey, fieldPerms]) => (
          <Card key={fieldKey} className="bg-white/5 border-white/10">
            <CardHeader className="p-3 pb-2">
              <CardTitle className="text-sm font-mono text-cyan-300">{fieldKey}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {fieldPerms.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 p-2 rounded bg-white/3">
                    <Switch
                      checked={p.can_edit}
                      onCheckedChange={(val) => toggleMutation.mutate({ id: p.id, canEdit: val })}
                      disabled={!isDraft}
                      data-testid={`switch-perm-${p.id}`}
                    />
                    <span className="text-white/70 text-xs">
                      {p.role_key} / {p.phase_key}
                    </span>
                    {p.can_edit && <CheckCircle2 className="w-3 h-3 text-green-400" />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Add Field Permission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Field Key</Label>
              <Input value={newFieldKey} onChange={(e) => setNewFieldKey(e.target.value)} placeholder="e.g. title" className="bg-white/5 border-white/10 text-white" data-testid="input-new-perm-field" />
            </div>
            <div>
              <Label className="text-white/80">Role</Label>
              <Select value={newRoleKey} onValueChange={setNewRoleKey}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-perm-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {V2_ROLE_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80">Phase</Label>
              <Select value={newPhaseKey} onValueChange={setNewPhaseKey}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-perm-phase"><SelectValue placeholder="Select phase" /></SelectTrigger>
                <SelectContent>
                  {V2_PHASE_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-white/80">Can Edit:</Label>
              <Switch checked={newCanEdit} onCheckedChange={setNewCanEdit} data-testid="switch-new-perm-edit" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={!newFieldKey || !newRoleKey || !newPhaseKey || addMutation.isPending}
              data-testid="button-confirm-add-perm"
            >
              {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransitionPermissionsTab({ versionId, isDraft }: { versionId: string; isDraft: boolean }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [addingToStatus, setAddingToStatus] = useState<string | null>(null);
  const [newPersonName, setNewPersonName] = useState("");

  const permsQuery = useQuery<{ data: TransitionPermissionRecord[] }>({
    queryKey: ["/api/governance-admin/versions", versionId, "transition-permissions"],
    queryFn: async () => {
      const res = await fetch(`/api/governance-admin/versions/${versionId}/transition-permissions`);
      return res.json();
    },
  });

  const membersQuery = useQuery<{ data: TeamMemberRecord[] }>({
    queryKey: ["/api/team-members"],
  });

  const perms = permsQuery.data?.data ?? [];
  const members = (membersQuery.data?.data ?? []).filter((m: TeamMemberRecord) => m.is_active);

  const destinationStatuses = V2_GOVERNANCE_CONTROLLED_DESTINATIONS;

  const groupedByDestination = useMemo(() => {
    return destinationStatuses.map((toStatus) => {
      const rules = perms.filter((p) => p.to_status === toStatus);
      return { toStatus, rules, isRestricted: rules.length > 0 };
    });
  }, [perms, destinationStatuses]);

  const addMutation = useMutation({
    mutationFn: async ({ toStatus, personName }: { toStatus: string; personName: string }) => {
      const res = await apiRequest("POST", "/api/governance-admin/transition-permissions", {
        governanceVersionId: versionId,
        toStatus,
        personName,
        canPerform: true,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "transition-permissions"] });
      setAddingToStatus(null);
      setNewPersonName("");
      toast({ title: "Authorized person added" });
    },
    onError: (err: any) => {
      showFromApiError("Add failed", err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/governance-admin/transition-permissions/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/governance-admin/versions", versionId, "transition-permissions"] });
      toast({ title: "Authorized person removed" });
    },
    onError: (err: any) => {
      showFromApiError("Remove failed", err);
    },
  });

  if (permsQuery.isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>;
  }

  const restricted = groupedByDestination.filter((g) => g.isRestricted);
  const unrestricted = groupedByDestination.filter((g) => !g.isRestricted);

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white" data-testid="text-transition-permissions-title">Transition Permissions</h2>
        <p className="text-white/50 text-sm mt-1">Control which team members are authorized to move items into each status. Statuses without assigned people remain open to all admins.</p>
      </div>

      <div className="space-y-4">
        {groupedByDestination.map((group) => {
          const existingNames = group.rules.map((r) => r.person_name);
          const availableMembers = members.filter((m: TeamMemberRecord) => !existingNames.includes(m.name));

          return (
            <Card
              key={group.toStatus}
              className={`border ${group.isRestricted ? "bg-white/5 border-amber-500/20" : "bg-white/[0.02] border-white/10"}`}
              data-testid={`card-destination-${group.toStatus}`}
            >
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className={`w-4 h-4 ${group.isRestricted ? "text-amber-400" : "text-white/30"}`} />
                    <span className="text-white font-medium">{V2_STATUS_LABELS[group.toStatus]}</span>
                    {group.isRestricted ? (
                      <Badge className="bg-amber-900/30 text-amber-300 border-amber-600/30 text-xs ml-2">
                        <Shield className="w-3 h-3 mr-1" />
                        Restricted
                      </Badge>
                    ) : (
                      <span className="text-white/30 text-xs ml-2 italic">Open to all admins</span>
                    )}
                  </div>
                  {isDraft && addingToStatus !== group.toStatus && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-cyan-400 hover:text-cyan-300 text-xs h-7 px-2"
                      onClick={() => { setAddingToStatus(group.toStatus); setNewPersonName(""); }}
                      data-testid={`button-add-person-${group.toStatus}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Authorized Person
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                {group.rules.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {group.rules.map((r) => (
                      <Badge
                        key={r.id}
                        className="bg-cyan-900/30 text-cyan-200 border-cyan-700/30 flex items-center gap-1 text-xs py-1 px-2"
                        data-testid={`badge-perm-${r.id}`}
                      >
                        <Users className="w-3 h-3 mr-0.5" />
                        {r.person_name}
                        {isDraft && (
                          <button
                            onClick={() => deleteMutation.mutate(r.id)}
                            className="ml-1 hover:text-red-400 transition-colors"
                            data-testid={`button-remove-perm-${r.id}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
                {isDraft && addingToStatus === group.toStatus && (
                  <div className="flex items-center gap-2 mt-2 p-2 rounded bg-white/5 border border-white/10">
                    <Select value={newPersonName} onValueChange={setNewPersonName}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white text-sm h-8 w-[220px]" data-testid="select-trans-person">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMembers.map((m: TeamMemberRecord) => (
                          <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      className="h-8"
                      onClick={() => addMutation.mutate({ toStatus: group.toStatus, personName: newPersonName })}
                      disabled={!newPersonName || addMutation.isPending}
                      data-testid="button-confirm-add-person"
                    >
                      {addMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-white/50"
                      onClick={() => { setAddingToStatus(null); setNewPersonName(""); }}
                      data-testid="button-cancel-add-person"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const HIRE_TYPE_OPTIONS: { value: string; label: string; pillClass: string }[] = [
  { value: "direct", label: "Direct", pillClass: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  { value: "contract", label: "Contract", pillClass: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  { value: "consultant", label: "Consultant", pillClass: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
];

function hireTypeMeta(value: string | null) {
  const v = (value ?? "direct").toLowerCase();
  return HIRE_TYPE_OPTIONS.find(o => o.value === v) ?? HIRE_TYPE_OPTIONS[0];
}

function TeamMembersTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMemberRecord | null>(null);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newTeamFunction, setNewTeamFunction] = useState("");
  const [newDepartmentId, setNewDepartmentId] = useState<string>("");
  const [newSectionId, setNewSectionId] = useState<string>("");
  const [newIsDepartmentHead, setNewIsDepartmentHead] = useState(false);
  const [newReportsToId, setNewReportsToId] = useState<string>("");
  const [newReportsToShowAll, setNewReportsToShowAll] = useState<boolean>(false);
  const [newHireType, setNewHireType] = useState<string>("direct");
  const [newSortOrder, setNewSortOrder] = useState(0);
  // v2.10.x — Org Chart tab additions
  const [hireTypeFilter, setHireTypeFilter] = useState<string>("all");
  const [showOrgChart, setShowOrgChart] = useState(false);

  const membersQuery = useQuery<{ data: TeamMemberRecord[] }>({
    queryKey: ["/api/team-members"],
  });
  const members = membersQuery.data?.data ?? [];

  const departmentsQuery = useQuery<{ data: DepartmentRecord[] }>({
    queryKey: ["/api/admin/departments"],
  });
  const departments = departmentsQuery.data?.data ?? [];
  const activeDepartments = departments.filter((d) => !d.is_archived);
  const sectionsForNewDept = activeDepartments
    .find((d) => d.id === newDepartmentId)?.sections.filter((s) => !s.is_archived) ?? [];

  const reportsToCandidatesNew = useMemo(() => {
    const same: TeamMemberRecord[] = [];
    const other: TeamMemberRecord[] = [];
    for (const m of members) {
      if (!m.is_active) continue;
      if (newDepartmentId && m.department_id === newDepartmentId) same.push(m);
      else other.push(m);
    }
    same.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    other.sort((a, b) => a.name.localeCompare(b.name));
    if (!newReportsToShowAll && newDepartmentId) return same;
    return [...same, ...other];
  }, [members, newDepartmentId, newReportsToShowAll]);

  const filteredMembers = useMemo(() => {
    if (hireTypeFilter === "all") return members;
    return members.filter(m => (m.hire_type ?? "direct") === hireTypeFilter);
  }, [members, hireTypeFilter]);

  const addMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/team-members", {
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        teamFunction: newTeamFunction.trim() || null,
        departmentId: newDepartmentId || null,
        sectionId: newSectionId || null,
        isDepartmentHead: newIsDepartmentHead && !!newDepartmentId,
        reportsToId: newReportsToId || null,
        hireType: newHireType || "direct",
        sortOrder: newSortOrder,
        isActive: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/people"] });
      toast({ title: "Team member added" });
      setShowAddDialog(false);
      setNewFirstName("");
      setNewLastName("");
      setNewTeamFunction("");
      setNewDepartmentId("");
      setNewSectionId("");
      setNewIsDepartmentHead(false);
      setNewReportsToId("");
      setNewHireType("direct");
      setNewSortOrder(0);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; body: Record<string, any> }) =>
      apiRequest("PATCH", `/api/team-members/${data.id}`, data.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/people"] });
      toast({ title: "Team member updated" });
      setEditingMember(null);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (data: { id: string; isActive: boolean }) =>
      apiRequest("PATCH", `/api/team-members/${data.id}`, { isActive: data.isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/people"] });
      toast({ title: "Status updated" });
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  if (membersQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white" data-testid="text-team-members-title">Org Chart</h2>
          <p className="text-sm text-white/50">Internal team members available for assignment, responsibility handoff, and chain-of-command rendering. Reports-to is display-only this release; it does not affect permissions, sign-off, or KPI rollup.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="text-cyan-300 border-cyan-500/40 hover:bg-cyan-500/10"
            onClick={() => setShowOrgChart(true)}
            disabled={members.length === 0}
            data-testid="button-view-org-chart"
          >
            <GitBranch className="w-4 h-4 mr-1" /> View Org Chart
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-team-member">
            <Plus className="w-4 h-4 mr-1" /> Add Member
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="filter-hire-type">
        <span className="text-xs text-white/45">Hire type:</span>
        {[{ value: "all", label: "All" }, ...HIRE_TYPE_OPTIONS].map((opt) => {
          const active = hireTypeFilter === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => setHireTypeFilter(opt.value)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${active ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/50" : "bg-white/5 text-white/60 border-white/10 hover:bg-white/10"}`}
              data-testid={`chip-hire-type-${opt.value}`}
            >
              {opt.label}
            </button>
          );
        })}
        {hireTypeFilter !== "all" && (
          <span className="text-xs text-white/40 ml-1">({filteredMembers.length} of {members.length})</span>
        )}
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/60">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Team / Function</th>
                <th className="text-left p-3">Department / Section</th>
                <th className="text-left p-3">Reports To</th>
                <th className="text-left p-3">Hire Type</th>
                <th className="text-left p-3">Scope</th>
                <th className="text-left p-3">Sort Order</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 && (
                <tr><td colSpan={9} className="text-center text-white/40 py-8">No team members match the current filter.</td></tr>
              )}
              {filteredMembers.map((m) => (
                <tr key={m.id} className="border-b border-white/5 hover-elevate">
                  <td className="p-3 text-white font-medium" data-testid={`text-member-name-${m.id}`}>{m.name}</td>
                  <td className="p-3 text-white/70">{m.team_function || "\u2014"}</td>
                  <td className="p-3 text-white/70" data-testid={`text-member-dept-${m.id}`}>
                    {m.department_name
                      ? <>
                          <span className="text-white/85">{m.department_name}</span>
                          {m.section_name && <span className="text-white/40"> / {m.section_name}</span>}
                        </>
                      : "\u2014"}
                  </td>
                  <td className="p-3 text-white/70" data-testid={`text-member-reports-to-${m.id}`}>
                    {m.reports_to_name ? (
                      <span className="text-white/85">{m.reports_to_name}</span>
                    ) : (
                      <span className="text-white/40 text-xs">—</span>
                    )}
                  </td>
                  <td className="p-3" data-testid={`text-member-hire-type-${m.id}`}>
                    {(() => {
                      const meta = hireTypeMeta(m.hire_type);
                      return (
                        <Badge variant="outline" className={`text-xs ${meta.pillClass}`}>
                          {meta.label}
                        </Badge>
                      );
                    })()}
                  </td>
                  <td className="p-3">
                    {m.is_department_head && m.department_id ? (
                      <Badge
                        className="bg-cyan-500/20 text-cyan-200 border border-cyan-400/40"
                        data-testid={`badge-member-dept-head-${m.id}`}
                        title="Sees every action item assigned to anyone in this department"
                      >
                        Dept Head
                      </Badge>
                    ) : (
                      <span className="text-white/40 text-xs">Own tasks</span>
                    )}
                  </td>
                  <td className="p-3 text-white/70">{m.sort_order}</td>
                  <td className="p-3">
                    <Badge variant={m.is_active ? "default" : "secondary"} data-testid={`badge-member-status-${m.id}`}>
                      {m.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingMember(m)}
                        data-testid={`button-edit-member-${m.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-white/60" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => toggleActiveMutation.mutate({ id: m.id, isActive: !m.is_active })}
                        data-testid={`button-toggle-member-${m.id}`}
                      >
                        {m.is_active ? <Archive className="w-4 h-4 text-amber-400/60" /> : <Zap className="w-4 h-4 text-green-400/60" />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">First Name</Label>
                <Input
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="First name"
                  data-testid="input-new-member-first-name"
                />
              </div>
              <div>
                <Label className="text-white/80">Last Name</Label>
                <Input
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Last name"
                  data-testid="input-new-member-last-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/80">Team / Function</Label>
              <Input
                value={newTeamFunction}
                onChange={(e) => setNewTeamFunction(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                placeholder="e.g. Investment Team, Operations"
                data-testid="input-new-member-function"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Department</Label>
                <Select
                  value={newDepartmentId || "__none__"}
                  onValueChange={(v) => {
                    const next = v === "__none__" ? "" : v;
                    setNewDepartmentId(next);
                    setNewSectionId("");
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-member-department">
                    <SelectValue placeholder="(none)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(none)</SelectItem>
                    {activeDepartments.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/80">Section</Label>
                <Select
                  value={newSectionId || "__none__"}
                  onValueChange={(v) => setNewSectionId(v === "__none__" ? "" : v)}
                  disabled={!newDepartmentId || sectionsForNewDept.length === 0}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-member-section">
                    <SelectValue placeholder={newDepartmentId ? (sectionsForNewDept.length === 0 ? "(no sections)" : "(none)") : "(pick department first)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">(none)</SelectItem>
                    {sectionsForNewDept.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-start gap-2 rounded border border-white/10 bg-white/5 p-3">
              <input
                type="checkbox"
                id="new-member-dept-head"
                className="mt-0.5 h-4 w-4 accent-cyan-500"
                checked={newIsDepartmentHead}
                disabled={!newDepartmentId}
                onChange={(e) => setNewIsDepartmentHead(e.target.checked)}
                data-testid="checkbox-new-member-dept-head"
              />
              <div className="flex-1">
                <Label htmlFor="new-member-dept-head" className="text-white/85 cursor-pointer">
                  Department head — sees all tasks in this department
                </Label>
                <p className="text-xs text-white/50 mt-0.5">
                  When checked, the user account linked to this team member will see every action item assigned to or currently with anyone in the selected department, in addition to their own tasks. Leave unchecked for regular members. Requires a department to be selected.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/80">Reports To</Label>
                <Select
                  value={newReportsToId || "__none__"}
                  onValueChange={(v) => setNewReportsToId(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-member-reports-to">
                    <SelectValue placeholder="(top of chart)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">(top of chart — no manager)</SelectItem>
                    {reportsToCandidatesNew.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}{m.team_function ? ` — ${m.team_function}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="mt-1 flex items-center gap-1.5 text-xs text-white/55 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3 w-3 accent-cyan-500"
                    checked={newReportsToShowAll}
                    onChange={(e) => setNewReportsToShowAll(e.target.checked)}
                    data-testid="checkbox-new-member-reports-to-show-all"
                  />
                  Show all team members
                </label>
              </div>
              <div>
                <Label className="text-white/80">Hire Type</Label>
                <Select value={newHireType} onValueChange={setNewHireType}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-member-hire-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HIRE_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={newSortOrder}
                onChange={(e) => setNewSortOrder(parseInt(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-member-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addMutation.mutate()}
              disabled={!newFirstName.trim() || !newLastName.trim() || addMutation.isPending}
              data-testid="button-confirm-add-member"
            >
              {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingMember} onOpenChange={(open) => !open && setEditingMember(null)}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Team Member</DialogTitle>
          </DialogHeader>
          {editingMember && (
            <>
              <EditMemberForm
                member={editingMember}
                departments={activeDepartments}
                allMembers={members}
                onSave={(body) => updateMutation.mutate({ id: editingMember.id, body })}
                isPending={updateMutation.isPending}
              />
              <EaAssignmentsEditor member={editingMember} allMembers={members} />
            </>
          )}
        </DialogContent>
      </Dialog>

      {showOrgChart && (
        <OrgChartModal members={members} onClose={() => setShowOrgChart(false)} />
      )}
    </div>
  );
}

function EditMemberForm({ member, departments, allMembers, onSave, isPending }: { member: TeamMemberRecord; departments: DepartmentRecord[]; allMembers: TeamMemberRecord[]; onSave: (body: Record<string, any>) => void; isPending: boolean }) {
  const [firstName, setFirstName] = useState(member.first_name || "");
  const [lastName, setLastName] = useState(member.last_name || "");
  const [teamFunction, setTeamFunction] = useState(member.team_function || "");
  const [departmentId, setDepartmentId] = useState<string>(member.department_id || "");
  const [sectionId, setSectionId] = useState<string>(member.section_id || "");
  const [isDepartmentHead, setIsDepartmentHead] = useState<boolean>(!!member.is_department_head);
  const [reportsToId, setReportsToId] = useState<string>(member.reports_to_id || "");
  const [reportsToShowAll, setReportsToShowAll] = useState<boolean>(false);
  const [hireType, setHireType] = useState<string>((member.hire_type ?? "direct").toLowerCase());
  const [sortOrder, setSortOrder] = useState(member.sort_order);

  // Excludes self, transitive descendants, and inactive members
  // (except the current value, so it can be cleared).
  const reportsToCandidates = useMemo(() => {
    const descendants = new Set<string>([member.id]);
    let frontier = [member.id];
    while (frontier.length) {
      const next: string[] = [];
      for (const f of frontier) {
        for (const m of allMembers) {
          if (m.reports_to_id === f && !descendants.has(m.id)) {
            descendants.add(m.id);
            next.push(m.id);
          }
        }
      }
      frontier = next;
    }
    const same: TeamMemberRecord[] = [];
    const other: TeamMemberRecord[] = [];
    for (const m of allMembers) {
      if (descendants.has(m.id)) continue;
      if (!m.is_active && m.id !== reportsToId) continue;
      if (departmentId && m.department_id === departmentId) same.push(m);
      else other.push(m);
    }
    same.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    other.sort((a, b) => a.name.localeCompare(b.name));
    if (!reportsToShowAll && departmentId) return same;
    return [...same, ...other];
  }, [allMembers, member.id, departmentId, reportsToId, reportsToShowAll]);

  // Build the section list. If the member's current dept happens to be
  // archived (so it's not in the active list), still show it so the user
  // can see what's currently assigned and clear or change it.
  const allDepts = departments.slice();
  if (member.department_id && !allDepts.find((d) => d.id === member.department_id)) {
    // Synthesise a placeholder option from the existing label so the
    // dropdown can render the saved value without losing it.
    allDepts.push({
      id: member.department_id,
      name: member.department_name || "(archived department)",
      sort_order: 0,
      is_archived: true,
      created_at: "",
      sections: [],
    });
  }
  const activeSections = allDepts.find((d) => d.id === departmentId)?.sections.filter((s) => !s.is_archived) ?? [];
  // Same fallback for an archived section that's still attached to this member.
  const sectionList = activeSections.slice();
  if (sectionId && !sectionList.find((s) => s.id === sectionId)) {
    sectionList.push({
      id: sectionId,
      department_id: departmentId,
      name: member.section_name || "(archived section)",
      sort_order: 0,
      is_archived: true,
      created_at: "",
    });
  }

  return (
    <>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/80">First Name</Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-edit-member-first-name"
            />
          </div>
          <div>
            <Label className="text-white/80">Last Name</Label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-edit-member-last-name"
            />
          </div>
        </div>
        <div>
          <Label className="text-white/80">Team / Function</Label>
          <Input
            value={teamFunction}
            onChange={(e) => setTeamFunction(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            data-testid="input-edit-member-function"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/80">Department</Label>
            <Select
              value={departmentId || "__none__"}
              onValueChange={(v) => {
                const next = v === "__none__" ? "" : v;
                setDepartmentId(next);
                setSectionId("");
              }}
            >
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-member-department">
                <SelectValue placeholder="(none)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">(none)</SelectItem>
                {allDepts.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}{d.is_archived ? " (archived)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/80">Section</Label>
            <Select
              value={sectionId || "__none__"}
              onValueChange={(v) => setSectionId(v === "__none__" ? "" : v)}
              disabled={!departmentId || sectionList.length === 0}
            >
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-member-section">
                <SelectValue placeholder={departmentId ? (sectionList.length === 0 ? "(no sections)" : "(none)") : "(pick department first)"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">(none)</SelectItem>
                {sectionList.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}{s.is_archived ? " (archived)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-start gap-2 rounded border border-white/10 bg-white/5 p-3">
          <input
            type="checkbox"
            id={`edit-member-dept-head-${member.id}`}
            className="mt-0.5 h-4 w-4 accent-cyan-500"
            checked={isDepartmentHead}
            disabled={!departmentId}
            onChange={(e) => setIsDepartmentHead(e.target.checked)}
            data-testid="checkbox-edit-member-dept-head"
          />
          <div className="flex-1">
            <Label htmlFor={`edit-member-dept-head-${member.id}`} className="text-white/85 cursor-pointer">
              Department head — sees all tasks in this department
            </Label>
            <p className="text-xs text-white/50 mt-0.5">
              When checked, the user account linked to this team member will see every action item assigned to or currently with anyone in the selected department, in addition to their own tasks. Leave unchecked for regular members. Requires a department to be selected.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/80">Reports To</Label>
            <Select
              value={reportsToId || "__none__"}
              onValueChange={(v) => setReportsToId(v === "__none__" ? "" : v)}
            >
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-member-reports-to">
                <SelectValue placeholder="(top of chart)" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">(top of chart — no manager)</SelectItem>
                {reportsToCandidates.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name}{m.team_function ? ` — ${m.team_function}` : ""}{!m.is_active ? " (inactive)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="mt-1 flex items-center gap-1.5 text-xs text-white/55 cursor-pointer">
              <input
                type="checkbox"
                className="h-3 w-3 accent-cyan-500"
                checked={reportsToShowAll}
                onChange={(e) => setReportsToShowAll(e.target.checked)}
                data-testid="checkbox-edit-member-reports-to-show-all"
              />
              Show all team members
            </label>
          </div>
          <div>
            <Label className="text-white/80">Hire Type</Label>
            <Select value={hireType} onValueChange={setHireType}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-member-hire-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HIRE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Sort Order</Label>
          <Input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            className="bg-white/5 border-white/10 text-white"
            data-testid="input-edit-member-sort"
          />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={() => onSave({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            teamFunction: teamFunction.trim() || null,
            departmentId: departmentId || null,
            sectionId: sectionId || null,
            isDepartmentHead: isDepartmentHead && !!departmentId,
            reportsToId: reportsToId || null,
            hireType: hireType || "direct",
            sortOrder,
          })}
          disabled={!firstName.trim() || !lastName.trim() || isPending}
          data-testid="button-confirm-edit-member"
        >
          {isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </DialogFooter>
    </>
  );
}

interface EaAssignmentRecord {
  id: string;
  eaTeamMemberId: string;
  principalTeamMemberId: string;
  canSignoff: boolean;
  eaName: string;
  principalName: string;
}

function EaAssignmentsEditor({ member, allMembers }: { member: TeamMemberRecord; allMembers: TeamMemberRecord[] }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [addPrincipalId, setAddPrincipalId] = useState<string>("");

  const assignmentsQuery = useQuery<{ data: EaAssignmentRecord[] }>({
    queryKey: ["/api/admin/ea-assignments"],
  });
  const myAssignments = (assignmentsQuery.data?.data ?? []).filter(
    (a) => a.eaTeamMemberId === member.id,
  );
  const assignedPrincipalIds = new Set(myAssignments.map((a) => a.principalTeamMemberId));

  const candidates = allMembers
    .filter((m) => m.is_active && m.id !== member.id && !assignedPrincipalIds.has(m.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["/api/admin/ea-assignments"] });

  const addMutation = useMutation({
    mutationFn: (principalTeamMemberId: string) =>
      apiRequest("POST", "/api/admin/ea-assignments", {
        eaTeamMemberId: member.id,
        principalTeamMemberId,
        canSignoff: false,
      }),
    onSuccess: () => {
      invalidate();
      setAddPrincipalId("");
      toast({ title: "Delegation added" });
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const signoffMutation = useMutation({
    mutationFn: (data: { id: string; canSignoff: boolean }) =>
      apiRequest("PATCH", `/api/admin/ea-assignments/${data.id}`, { canSignoff: data.canSignoff }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Sign-off authority updated" });
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/ea-assignments/${id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Delegation removed" });
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  return (
    <div className="mt-5 border-t border-white/10 pt-4" data-testid="section-ea-assignments">
      <Label className="text-white/80">Acts as assistant for</Label>
      <p className="text-xs text-white/45 mb-3">
        Principals this member may act on behalf of (register, hand off, submit to MD, return, change
        status). Sign-off authority is off by default and granted per principal.
      </p>

      {assignmentsQuery.isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>
      ) : myAssignments.length === 0 ? (
        <p className="text-xs text-white/35 mb-3" data-testid="text-no-ea-assignments">No delegations yet.</p>
      ) : (
        <div className="space-y-2 mb-3">
          {myAssignments.map((a) => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded px-3 py-2"
              data-testid={`row-ea-assignment-${a.id}`}
            >
              <span className="text-sm text-white" data-testid={`text-ea-principal-${a.id}`}>{a.principalName}</span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer">
                  <Switch
                    checked={a.canSignoff}
                    onCheckedChange={(checked) => signoffMutation.mutate({ id: a.id, canSignoff: checked })}
                    data-testid={`switch-ea-signoff-${a.id}`}
                  />
                  Can sign off
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-300 hover:text-red-200 hover:bg-red-500/10 h-7 px-2"
                  onClick={() => deleteMutation.mutate(a.id)}
                  disabled={deleteMutation.isPending}
                  data-testid={`button-remove-ea-${a.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Select value={addPrincipalId || "__none__"} onValueChange={(v) => setAddPrincipalId(v === "__none__" ? "" : v)}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white flex-1" data-testid="select-add-ea-principal">
            <SelectValue placeholder="Add a principal…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Add a principal…</SelectItem>
            {candidates.map((m) => (
              <SelectItem key={m.id} value={m.id} data-testid={`option-ea-principal-${m.id}`}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={() => addMutation.mutate(addPrincipalId)}
          disabled={!addPrincipalId || addMutation.isPending}
          data-testid="button-add-ea-assignment"
        >
          {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

interface UserAccountRecord {
  id: string;
  username: string;
  email: string | null;
  firstName: string;
  lastName: string;
  role: string;
  teamMemberId: string | null;
  teamMemberName: string | null;
  canRegisterTasks: boolean;
}

const ROLES_THAT_INHERENTLY_REGISTER = new Set([
  "general_admin", "admin", "hr_admin", "it_admin", "super_admin", "dev_admin",
]);

type DepartmentSectionRecord = {
  id: string;
  department_id: string;
  name: string;
  sort_order: number;
  is_archived: boolean;
  created_at: string;
};
type DepartmentRecord = {
  id: string;
  name: string;
  sort_order: number;
  is_archived: boolean;
  created_at: string;
  sections: DepartmentSectionRecord[];
};

function DepartmentsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [showAddDeptDialog, setShowAddDeptDialog] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptSort, setNewDeptSort] = useState(0);

  const [editingDept, setEditingDept] = useState<DepartmentRecord | null>(null);
  const [editDeptName, setEditDeptName] = useState("");
  const [editDeptSort, setEditDeptSort] = useState(0);

  const [addSectionForDeptId, setAddSectionForDeptId] = useState<string | null>(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionSort, setNewSectionSort] = useState(0);

  const [editingSection, setEditingSection] = useState<DepartmentSectionRecord | null>(null);
  const [editSectionName, setEditSectionName] = useState("");
  const [editSectionSort, setEditSectionSort] = useState(0);

  const [showArchived, setShowArchived] = useState(false);

  const deptsQuery = useQuery<{ data: DepartmentRecord[] }>({
    queryKey: ["/api/admin/departments"],
  });
  const departments = deptsQuery.data?.data ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/departments"] });

  const addDeptMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/departments", {
      name: newDeptName.trim(),
      sortOrder: newDeptSort,
    }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Department added" });
      setShowAddDeptDialog(false);
      setNewDeptName("");
      setNewDeptSort(0);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const updateDeptMutation = useMutation({
    mutationFn: (data: { id: string; body: Record<string, any> }) =>
      apiRequest("PATCH", `/api/admin/departments/${data.id}`, data.body),
    onSuccess: () => {
      invalidate();
      toast({ title: "Department updated" });
      setEditingDept(null);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const deleteDeptMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/departments/${id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Department deleted" });
    },
    onError: (err: any) => showFromApiError("Cannot delete department", err),
  });

  const addSectionMutation = useMutation({
    mutationFn: (data: { deptId: string; name: string; sortOrder: number }) =>
      apiRequest("POST", `/api/admin/departments/${data.deptId}/sections`, {
        name: data.name,
        sortOrder: data.sortOrder,
      }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Section added" });
      setAddSectionForDeptId(null);
      setNewSectionName("");
      setNewSectionSort(0);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const updateSectionMutation = useMutation({
    mutationFn: (data: { id: string; body: Record<string, any> }) =>
      apiRequest("PATCH", `/api/admin/department-sections/${data.id}`, data.body),
    onSuccess: () => {
      invalidate();
      toast({ title: "Section updated" });
      setEditingSection(null);
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const deleteSectionMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/department-sections/${id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Section deleted" });
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  if (deptsQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  const visibleDepts = showArchived ? departments : departments.filter((d) => !d.is_archived);

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white" data-testid="text-departments-title">Departments</h2>
          <p className="text-sm text-white/50">Organisational structure: departments and the sections under each. Used to prepare for inter-departmental task tracking. Not yet wired into the Register form, KPIs, or task flow.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="checkbox-show-archived-departments"
            />
            Show archived
          </label>
          <Button onClick={() => setShowAddDeptDialog(true)} data-testid="button-add-department">
            <Plus className="w-4 h-4 mr-1" /> Add Department
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {visibleDepts.length === 0 && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-8 text-center text-white/40">No departments defined yet.</CardContent>
          </Card>
        )}
        {visibleDepts.map((dept) => {
          const visibleSections = showArchived ? dept.sections : dept.sections.filter((s) => !s.is_archived);
          return (
            <Card key={dept.id} className={`bg-white/5 border-white/10 ${dept.is_archived ? "opacity-50" : ""}`}>
              <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <CardTitle className="text-white text-base" data-testid={`text-department-name-${dept.id}`}>
                    {dept.name}
                  </CardTitle>
                  <Badge variant="secondary" className="bg-white/10 text-white/60">
                    Sort: {dept.sort_order}
                  </Badge>
                  {dept.is_archived && (
                    <Badge variant="secondary" className="bg-amber-900/30 text-amber-300">Archived</Badge>
                  )}
                  <span className="text-xs text-white/40">
                    {visibleSections.length} section{visibleSections.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-white/70"
                    onClick={() => {
                      setAddSectionForDeptId(dept.id);
                      setNewSectionName("");
                      setNewSectionSort((dept.sections.length + 1));
                    }}
                    data-testid={`button-add-section-${dept.id}`}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Section
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setEditingDept(dept);
                      setEditDeptName(dept.name);
                      setEditDeptSort(dept.sort_order);
                    }}
                    data-testid={`button-edit-department-${dept.id}`}
                  >
                    <Edit2 className="w-4 h-4 text-white/60" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => updateDeptMutation.mutate({ id: dept.id, body: { isArchived: !dept.is_archived } })}
                    title={dept.is_archived ? "Unarchive" : "Archive"}
                    data-testid={`button-archive-department-${dept.id}`}
                  >
                    {dept.is_archived
                      ? <Zap className="w-4 h-4 text-green-400/70" />
                      : <Archive className="w-4 h-4 text-amber-400/60" />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Permanently delete the department "${dept.name}"? This cannot be undone. (Departments with any sections cannot be deleted — archive instead.)`)) {
                        deleteDeptMutation.mutate(dept.id);
                      }
                    }}
                    title="Delete (only if no sections)"
                    data-testid={`button-delete-department-${dept.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400/60" />
                  </Button>
                </div>
              </CardHeader>
              {visibleSections.length > 0 && (
                <CardContent className="pt-0">
                  <div className="space-y-1 pl-4 border-l border-white/10">
                    {visibleSections.map((section) => (
                      <div
                        key={section.id}
                        className={`flex items-center justify-between gap-3 py-1.5 px-2 rounded hover-elevate ${section.is_archived ? "opacity-50" : ""}`}
                        data-testid={`row-section-${section.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-white/40">—</span>
                          <span className="text-white/85" data-testid={`text-section-name-${section.id}`}>
                            {section.name}
                          </span>
                          <span className="text-xs text-white/30">Sort: {section.sort_order}</span>
                          {section.is_archived && (
                            <Badge variant="secondary" className="bg-amber-900/30 text-amber-300 text-xs">Archived</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditingSection(section);
                              setEditSectionName(section.name);
                              setEditSectionSort(section.sort_order);
                            }}
                            data-testid={`button-edit-section-${section.id}`}
                          >
                            <Edit2 className="w-3.5 h-3.5 text-white/60" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => updateSectionMutation.mutate({ id: section.id, body: { isArchived: !section.is_archived } })}
                            title={section.is_archived ? "Unarchive" : "Archive"}
                            data-testid={`button-archive-section-${section.id}`}
                          >
                            {section.is_archived
                              ? <Zap className="w-3.5 h-3.5 text-green-400/70" />
                              : <Archive className="w-3.5 h-3.5 text-amber-400/60" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (confirm(`Permanently delete the section "${section.name}"? This cannot be undone.`)) {
                                deleteSectionMutation.mutate(section.id);
                              }
                            }}
                            data-testid={`button-delete-section-${section.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400/60" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={showAddDeptDialog} onOpenChange={setShowAddDeptDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add Department</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/80">Department Name</Label>
              <Input
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                placeholder="e.g. Strategy and Corporate Affairs"
                data-testid="input-new-department-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={newDeptSort}
                onChange={(e) => setNewDeptSort(parseInt(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-department-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setShowAddDeptDialog(false)}>Cancel</Button>
            <Button
              onClick={() => addDeptMutation.mutate()}
              disabled={!newDeptName.trim() || addDeptMutation.isPending}
              data-testid="button-confirm-add-department"
            >
              {addDeptMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingDept} onOpenChange={(open) => !open && setEditingDept(null)}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Department</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/80">Department Name</Label>
              <Input
                value={editDeptName}
                onChange={(e) => setEditDeptName(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-edit-department-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={editDeptSort}
                onChange={(e) => setEditDeptSort(parseInt(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-edit-department-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setEditingDept(null)}>Cancel</Button>
            <Button
              onClick={() => editingDept && updateDeptMutation.mutate({ id: editingDept.id, body: { name: editDeptName.trim(), sortOrder: editDeptSort } })}
              disabled={!editDeptName.trim() || updateDeptMutation.isPending}
              data-testid="button-confirm-edit-department"
            >
              {updateDeptMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addSectionForDeptId} onOpenChange={(open) => !open && setAddSectionForDeptId(null)}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">
              Add Section{addSectionForDeptId && (() => {
                const d = departments.find((x) => x.id === addSectionForDeptId);
                return d ? ` to "${d.name}"` : "";
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/80">Section Name</Label>
              <Input
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                placeholder="e.g. Corporate Communication"
                data-testid="input-new-section-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={newSectionSort}
                onChange={(e) => setNewSectionSort(parseInt(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-section-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setAddSectionForDeptId(null)}>Cancel</Button>
            <Button
              onClick={() => addSectionForDeptId && addSectionMutation.mutate({ deptId: addSectionForDeptId, name: newSectionName.trim(), sortOrder: newSectionSort })}
              disabled={!newSectionName.trim() || addSectionMutation.isPending}
              data-testid="button-confirm-add-section"
            >
              {addSectionMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingSection} onOpenChange={(open) => !open && setEditingSection(null)}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/80">Section Name</Label>
              <Input
                value={editSectionName}
                onChange={(e) => setEditSectionName(e.target.value)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-edit-section-name"
              />
            </div>
            <div>
              <Label className="text-white/80">Sort Order</Label>
              <Input
                type="number"
                value={editSectionSort}
                onChange={(e) => setEditSectionSort(parseInt(e.target.value) || 0)}
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-edit-section-sort"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-white/60" onClick={() => setEditingSection(null)}>Cancel</Button>
            <Button
              onClick={() => editingSection && updateSectionMutation.mutate({ id: editingSection.id, body: { name: editSectionName.trim(), sortOrder: editSectionSort } })}
              disabled={!editSectionName.trim() || updateSectionMutation.isPending}
              data-testid="button-confirm-edit-section"
            >
              {updateSectionMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserAccountsTab() {
  const { toast } = useToast();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAccountRecord | null>(null);

  const usersQuery = useQuery<{ data: UserAccountRecord[] }>({
    queryKey: ["/api/admin/users"],
  });
  const users = usersQuery.data?.data ?? [];

  const teamMembersQuery = useQuery<{ data: TeamMemberRecord[] }>({
    queryKey: ["/api/team-members"],
  });
  const teamMembers = (teamMembersQuery.data?.data ?? []).filter((m) => m.is_active);

  // Friendly label lookup for the Role badge column. Covers preset + custom
  // roles (custom roles aren't in the hard-coded ROLE_LABELS map). Falls back
  // through ROLE_LABELS, then the raw role name, so unknown roles still
  // render something instead of going blank.
  const roleLabelsQuery = useQuery<{ data: { name: string; label: string }[] }>({
    queryKey: ["/api/admin/role-labels"],
  });
  const roleLabelMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roleLabelsQuery.data?.data ?? []) m.set(r.name, r.label);
    return m;
  }, [roleLabelsQuery.data]);
  function labelForRole(name: string): string {
    return roleLabelMap.get(name) || ROLE_LABELS[name] || name;
  }

  if (usersQuery.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white" data-testid="text-user-accounts-title">User Accounts</h2>
          <p className="text-sm text-white/50">Login credentials for system access. Link each account to a team member to control which items they can see and update.</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="button-add-user">
          <Plus className="w-4 h-4 mr-1" /> Add User
        </Button>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/60">
                <th className="text-left p-3">Username</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Linked Team Member</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center text-white/40 py-8">No user accounts found.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover-elevate">
                  <td className="p-3 text-white font-medium" data-testid={`text-user-username-${u.id}`}>{u.username}</td>
                  <td className="p-3 text-white/70">{[u.firstName, u.lastName].filter(Boolean).join(" ") || "\u2014"}</td>
                  <td className="p-3 text-white/70" data-testid={`text-user-email-${u.id}`}>{u.email || "\u2014"}</td>
                  <td className="p-3">
                    <Badge
                      variant={u.role === "member" ? "secondary" : "default"}
                      className={
                        u.role === "dev_admin" ? "bg-red-600/80 text-white" :
                        u.role === "super_admin" ? "bg-purple-600/80 text-white" :
                        u.role === "it_admin" ? "bg-blue-600/80 text-white" :
                        u.role === "hr_admin" ? "bg-teal-600/80 text-white" :
                        u.role === "general_admin" || u.role === "admin" ? "bg-green-600/80 text-white" :
                        ""
                      }
                      data-testid={`badge-user-role-${u.id}`}
                    >
                      {labelForRole(u.role)}
                    </Badge>
                  </td>
                  <td className="p-3 text-white/70" data-testid={`text-user-team-member-${u.id}`}>{u.teamMemberName || "\u2014"}</td>
                  <td className="p-3 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setEditingUser(u)}
                      data-testid={`button-edit-user-${u.id}`}
                    >
                      <Edit2 className="w-4 h-4 text-white/60" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Add User Account</DialogTitle>
          </DialogHeader>
          <AddUserForm
            teamMembers={teamMembers}
            onClose={() => setShowAddDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="bg-[#1a1a3e] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Edit User Account</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <EditUserForm
              user={editingUser}
              teamMembers={teamMembers}
              onClose={() => setEditingUser(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AddUserForm({ teamMembers, onClose }: { teamMembers: TeamMemberRecord[]; onClose: () => void }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { isGeneralAdmin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("member");
  const [teamMemberId, setTeamMemberId] = useState<string>("");
  const [canRegisterTasks, setCanRegisterTasks] = useState(false);
  const roleInherentlyRegisters = ROLES_THAT_INHERENTLY_REGISTER.has(role);
  const effectiveCanRegister = roleInherentlyRegisters || canRegisterTasks;

  const assignableRolesQuery = useQuery<{ data: { name: string; label: string }[] }>({
    queryKey: ["/api/admin/assignable-roles"],
  });
  const assignableRoles = assignableRolesQuery.data?.data ?? [{ name: "member", label: "Member" }];

  const addMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/users", {
        username: username.trim(),
        password,
        email: email.trim() || "",
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        teamMemberId: teamMemberId && teamMemberId !== "none" ? teamMemberId : null,
        canRegisterTasks: roleInherentlyRegisters ? false : canRegisterTasks,
        confirmPassword,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User account created" });
      onClose();
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-white/80">Username</Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            placeholder="e.g. arun.kumar"
            data-testid="input-new-user-username"
          />
          <p className="text-xs text-white/40 mt-1">Used to log in. Must be unique. Letters, numbers, dot, dash, underscore — no spaces.</p>
        </div>
        <div>
          <Label className="text-white/80">Password</Label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white pr-10"
              placeholder="Min 8 characters"
              data-testid="input-new-user-password"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute right-0 top-0"
              onClick={() => setShowPassword(!showPassword)}
              data-testid="button-toggle-password-visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4 text-white/40" /> : <Eye className="w-4 h-4 text-white/40" />}
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Email Address</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            placeholder="user@company.com"
            data-testid="input-new-user-email"
          />
          <p className="text-xs text-white/40 mt-1">Used for email notifications (updates, handoffs, new items).</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/80">First Name</Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-new-user-firstname"
            />
          </div>
          <div>
            <Label className="text-white/80">Last Name</Label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-new-user-lastname"
            />
          </div>
        </div>
        <div>
          <Label className="text-white/80">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-user-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {assignableRoles.map((r) => (
                <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/80">Link to Team Member</Label>
          <Select value={teamMemberId} onValueChange={setTeamMemberId}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-new-user-team-member">
              <SelectValue placeholder="Select team member..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No link</SelectItem>
              {teamMembers.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}{m.team_function ? ` (${m.team_function})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-white/40 mt-1">Linking determines which items this user sees on the Updates page.</p>
        </div>
        {isGeneralAdmin && (
          <div className="border-t border-white/10 pt-3 mt-2">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="add-user-can-register"
                className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed accent-cyan-500"
                checked={effectiveCanRegister}
                disabled={roleInherentlyRegisters}
                onChange={(e) => setCanRegisterTasks(e.target.checked)}
                data-testid="checkbox-add-user-can-register-tasks"
              />
              <div>
                <Label htmlFor="add-user-can-register" className="text-white/80 cursor-pointer">
                  Can register tasks
                </Label>
                <p className="text-xs text-white/40 mt-1">
                  {roleInherentlyRegisters
                    ? "Granted automatically by this user's role — cannot be turned off."
                    : "Allows this user to register new action items via the Register page and the Meeting Tasks page. Recommended for assistants, EAs, and trusted team members."}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="border-t border-white/10 pt-3 mt-2">
          <Label className="text-white/80">Your Admin Password (to confirm it's you)</Label>
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            placeholder="Enter YOUR password (the account you're logged in with)"
            data-testid="input-add-user-confirm-password"
          />
          <p className="text-xs text-amber-400/70 mt-1">This is your own password — the account you are currently signed in with — not the new user's.</p>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" className="text-white/60" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => addMutation.mutate()}
          disabled={!username.trim() || password.length < 8 || !confirmPassword || addMutation.isPending}
          data-testid="button-confirm-add-user"
        >
          {addMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Create Account
        </Button>
      </DialogFooter>
    </>
  );
}


function EditUserForm({ user, teamMembers, onClose }: { user: UserAccountRecord; teamMembers: TeamMemberRecord[]; onClose: () => void }) {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const { hasPermission, isGeneralAdmin } = useAuth();
  const canDeleteUsers = hasPermission("delete_users");
  const [username, setUsername] = useState(user.username || "");
  const [email, setEmail] = useState(user.email || "");
  const [firstName, setFirstName] = useState(user.firstName || "");
  const [lastName, setLastName] = useState(user.lastName || "");
  const [role, setRole] = useState(user.role);
  const [teamMemberId, setTeamMemberId] = useState(user.teamMemberId || "none");
  const [canRegisterTasks, setCanRegisterTasks] = useState(!!user.canRegisterTasks);
  const roleInherentlyRegisters = ROLES_THAT_INHERENTLY_REGISTER.has(role);
  const effectiveCanRegister = roleInherentlyRegisters || canRegisterTasks;
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState("");
  const [activeItemsWarning, setActiveItemsWarning] = useState<{ message: string; items: { refCode: string; title: string }[] } | null>(null);

  const assignableRolesQuery = useQuery<{ data: { name: string; label: string }[] }>({
    queryKey: ["/api/admin/assignable-roles"],
  });
  const assignableRoles = assignableRolesQuery.data?.data ?? [{ name: "member", label: "Member" }];
  const roleLabelsQuery = useQuery<{ data: { name: string; label: string }[] }>({
    queryKey: ["/api/admin/role-labels"],
  });
  const currentRoleLabel =
    assignableRoles.find((r) => r.name === user.role)?.label
    ?? roleLabelsQuery.data?.data.find((r) => r.name === user.role)?.label
    ?? ROLE_LABELS[user.role]
    ?? user.role;

  const updateMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, any> = {
        email: email.trim() || null,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        teamMemberId: teamMemberId && teamMemberId !== "none" ? teamMemberId : null,
      };
      if (isGeneralAdmin) {
        body.canRegisterTasks = roleInherentlyRegisters ? false : canRegisterTasks;
      }
      const trimmedUsername = username.trim();
      if (trimmedUsername && trimmedUsername !== (user.username || "")) {
        body.username = trimmedUsername;
      }
      if (newPassword) {
        body.password = newPassword;
        body.confirmPassword = confirmPassword;
      }
      return apiRequest("PATCH", `/api/admin/users/${user.id}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({ title: "User account updated" });
      onClose();
    },
    onError: (err: any) => showFromApiError("Error", err),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      return apiRequest("DELETE", `/api/admin/users/${user.id}`, { confirmPassword: deleteConfirmPassword });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/team-members"] });
      toast({ title: "User account and team member deleted" });
      onClose();
    },
    onError: async (err: any) => {
      const raw = err.message || "";
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          if (parsed.activeItems || (parsed.message && parsed.message.includes("active item"))) {
            setActiveItemsWarning({ message: parsed.message, items: parsed.activeItems || [] });
            return;
          }
          showFromApiError("Cannot Delete", { message: parsed.message || raw });
          return;
        } catch {}
      }
      showFromApiError("Cannot Delete", { message: raw });
    },
  });

  if (showDeleteConfirm) {
    return (
      <>
        <div className="space-y-4">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-red-300 font-semibold">Permanently Delete User</h3>
            </div>
            <p className="text-white/70 text-sm">
              You are about to permanently delete <strong className="text-white">{user.firstName} {user.lastName}</strong> ({user.username}).
            </p>
            <ul className="text-white/60 text-xs mt-2 space-y-1 list-disc list-inside">
              <li>Their user account (login) will be removed</li>
              {user.teamMemberName && <li>Their team member profile ({user.teamMemberName}) will be removed</li>}
              <li>They will no longer appear in future dropdowns</li>
              <li>All completed action items will remain attributed to them</li>
              <li>If re-created later with the same name, past history reconnects automatically</li>
            </ul>
          </div>

          {activeItemsWarning && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <h3 className="text-amber-300 font-semibold text-sm">Cannot Delete — Active Items</h3>
              </div>
              <p className="text-white/70 text-sm">{activeItemsWarning.message}</p>
              <p className="text-white/50 text-xs mt-2">Please reassign or handoff these items to another team member before deleting this user.</p>
            </div>
          )}

          <div>
            <Label className="text-white/80">Your Admin Password (to confirm it's you)</Label>
            <Input
              type="password"
              value={deleteConfirmPassword}
              onChange={(e) => setDeleteConfirmPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Enter YOUR password (the account you're logged in with)"
              data-testid="input-delete-user-confirm-password"
            />
            <p className="text-xs text-amber-400/70 mt-1">This is your own password — the account you are currently signed in with.</p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" className="text-white/60" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmPassword(""); setActiveItemsWarning(null); }}>
            Back
          </Button>
          <Button
            variant="destructive"
            onClick={() => deleteMutation.mutate()}
            disabled={!deleteConfirmPassword || deleteMutation.isPending}
            data-testid="button-confirm-delete-user"
          >
            {deleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Permanently Delete
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-white/80">Username</Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            placeholder="username"
            data-testid="input-edit-user-username"
          />
          <p className="text-xs text-white/40 mt-1">Used to log in. Must be unique. Letters, numbers, dot, dash, underscore — no spaces.</p>
        </div>
        <div>
          <Label className="text-white/80">Email Address</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white/5 border-white/10 text-white"
            placeholder="user@company.com"
            data-testid="input-edit-user-email"
          />
          <p className="text-xs text-white/40 mt-1">Used for email notifications.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-white/80">First Name</Label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-edit-user-firstname"
            />
          </div>
          <div>
            <Label className="text-white/80">Last Name</Label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              data-testid="input-edit-user-lastname"
            />
          </div>
        </div>
        <div>
          <Label className="text-white/80">Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-user-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {assignableRoles.map((r) => (
                <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
              ))}
              {!assignableRoles.some((r) => r.name === user.role) && (
                <SelectItem value={user.role} disabled>{currentRoleLabel} (current)</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/80">Link to Team Member</Label>
          <Select value={teamMemberId} onValueChange={setTeamMemberId}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="select-edit-user-team-member">
              <SelectValue placeholder="Select team member..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No link</SelectItem>
              {teamMembers.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}{m.team_function ? ` (${m.team_function})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-white/80">Reset Password</Label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white pr-10"
              placeholder="Leave blank to keep current"
              data-testid="input-edit-user-password"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="absolute right-0 top-0"
              onClick={() => setShowPassword(!showPassword)}
              data-testid="button-toggle-edit-password-visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4 text-white/40" /> : <Eye className="w-4 h-4 text-white/40" />}
            </Button>
          </div>
          <p className="text-xs text-white/40 mt-1">Only fill in if you want to change the password (min 8 characters).</p>
        </div>
        {isGeneralAdmin && (
          <div className="border-t border-white/10 pt-3 mt-2">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id={`edit-user-can-register-${user.id}`}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed accent-cyan-500"
                checked={effectiveCanRegister}
                disabled={roleInherentlyRegisters}
                onChange={(e) => setCanRegisterTasks(e.target.checked)}
                data-testid="checkbox-edit-user-can-register-tasks"
              />
              <div>
                <Label htmlFor={`edit-user-can-register-${user.id}`} className="text-white/80 cursor-pointer">
                  Can register tasks
                </Label>
                <p className="text-xs text-white/40 mt-1">
                  {roleInherentlyRegisters
                    ? "Granted automatically by this user's role — cannot be turned off."
                    : "Allows this user to register new action items via the Register page and the Meeting Tasks page. Recommended for assistants, EAs, and trusted team members."}
                </p>
              </div>
            </div>
          </div>
        )}
        {newPassword && (
          <div className="border-t border-white/10 pt-3 mt-2">
            <Label className="text-white/80">Your Admin Password (to confirm it's you)</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
              placeholder="Enter YOUR password (the account you're logged in with)"
              data-testid="input-edit-user-confirm-password"
            />
            <p className="text-xs text-amber-400/70 mt-1">This is your own password — the account you are currently signed in with — not the user whose password you're resetting.</p>
          </div>
        )}
      </div>
      {canDeleteUsers && (
        <div className="border-t border-white/10 pt-3 mt-2">
          <Button
            variant="ghost"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs px-2 py-1"
            onClick={() => setShowDeleteConfirm(true)}
            data-testid="button-show-delete-user"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Delete this user account
          </Button>
        </div>
      )}
      <DialogFooter>
        <Button variant="ghost" className="text-white/60" onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => updateMutation.mutate()}
          disabled={updateMutation.isPending || (newPassword !== "" && (newPassword.length < 8 || !confirmPassword))}
          data-testid="button-confirm-edit-user"
        >
          {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
          Save
        </Button>
      </DialogFooter>
    </>
  );
}

interface KpiRecord {
  name: string;
  teamFunction: string;
  tasksAssigned: number;
  tasksCurrentlyWith: number;
  tasksCompleted: number;
  tasksDeleted: number;
  tasksOverdue: number;
  deadlinesMet: number;
  deadlinesMissed: number;
  deadlinesMetByOriginal: number;
  deadlinesMissedByOriginal: number;
  timesReturnedForAmendments: number;
  tasksHandedTo: number;
  tasksHandedFrom: number;
  tasksReassignedAwayInsufficientProgress: number;
  totalHandlingTimeHours: number;
  handoffCount: number;
  avgHandlingTimeHours: number;
  subTasksAssigned: number;
  subTasksCompleted: number;
  subTasksOverdue: number;
  subTaskDeadlinesMet: number;
  subTaskDeadlinesMissed: number;
}

function KpiDashboardTab() {
  const { hasPermission } = useAuth();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("ALL");
  const [selectedDept, setSelectedDept] = useState("ALL");

  const kpiDeptsQuery = useQuery<{ data: DepartmentRecord[] }>({
    queryKey: ["/api/admin/departments"],
  });
  const kpiActiveDepts = (kpiDeptsQuery.data?.data ?? []).filter((d) => !d.is_archived);

  const queryParams = new URLSearchParams();
  if (dateFrom) queryParams.set("dateFrom", dateFrom);
  if (dateTo) queryParams.set("dateTo", dateTo);
  if (selectedDept !== "ALL") queryParams.set("department", selectedDept);
  const queryString = queryParams.toString();

  // Phase 4: response now also carries `gradingMode` ("board" | "internal")
  // so the dashboard can show a small badge near the On-Time Rate tile
  // explaining which date the on-time count was graded against.
  const kpiQuery = useQuery<{ data: KpiRecord[]; gradingMode?: "board" | "internal" }>({
    queryKey: ["/api/admin/kpi", queryString],
    queryFn: async () => {
      const res = await fetch(`/api/admin/kpi${queryString ? `?${queryString}` : ""}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load KPI data");
      return res.json();
    },
  });

  const kpiData = kpiQuery.data?.data || [];
  const gradingMode = kpiQuery.data?.gradingMode || "board";
  const filteredData = selectedPerson === "ALL" ? kpiData : kpiData.filter(p => p.name === selectedPerson);

  const totals = useMemo(() => {
    return filteredData.reduce((acc, p) => ({
      tasksAssigned: acc.tasksAssigned + p.tasksAssigned,
      tasksCompleted: acc.tasksCompleted + p.tasksCompleted,
      tasksOverdue: acc.tasksOverdue + p.tasksOverdue,
      deadlinesMet: acc.deadlinesMet + p.deadlinesMet,
      deadlinesMissed: acc.deadlinesMissed + p.deadlinesMissed,
      deadlinesMetByOriginal: acc.deadlinesMetByOriginal + (p.deadlinesMetByOriginal || 0),
      deadlinesMissedByOriginal: acc.deadlinesMissedByOriginal + (p.deadlinesMissedByOriginal || 0),
      timesReturned: acc.timesReturned + p.timesReturnedForAmendments,
    }), { tasksAssigned: 0, tasksCompleted: 0, tasksOverdue: 0, deadlinesMet: 0, deadlinesMissed: 0, deadlinesMetByOriginal: 0, deadlinesMissedByOriginal: 0, timesReturned: 0 });
  }, [filteredData]);

  const onTimeRate = totals.deadlinesMet + totals.deadlinesMissed > 0
    ? Math.round((totals.deadlinesMet / (totals.deadlinesMet + totals.deadlinesMissed)) * 100)
    : 0;

  const onOriginalRate = totals.deadlinesMetByOriginal + totals.deadlinesMissedByOriginal > 0
    ? Math.round((totals.deadlinesMetByOriginal / (totals.deadlinesMetByOriginal + totals.deadlinesMissedByOriginal)) * 100)
    : 0;

  function formatHours(hours: number): string {
    if (hours === 0) return "-";
    if (hours < 24) return `${hours}h`;
    const days = Math.round(hours / 24 * 10) / 10;
    return `${days}d`;
  }

  if (kpiQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-white" data-testid="text-kpi-title">KPI Dashboard</h2>
        <p className="text-sm text-white/50 mt-1">Performance metrics for team members based on task assignments, completions, deadlines, and handoffs.</p>
      </div>

      <div className="flex items-end gap-3 mb-6 flex-wrap">
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs">From Date</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-white/5 border-white/10 text-white text-sm w-40"
            data-testid="input-kpi-date-from"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs">To Date</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-white/5 border-white/10 text-white text-sm w-40"
            data-testid="input-kpi-date-to"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs">Department</Label>
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white w-48" data-testid="select-kpi-department">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1a1a3e] border-white/15">
              <SelectItem value="ALL" className="text-white">All Departments</SelectItem>
              {kpiActiveDepts.map((d) => (
                <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/60 text-xs">Team Member</Label>
          <Select value={selectedPerson} onValueChange={setSelectedPerson}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white w-48" data-testid="select-kpi-person">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-white">All Members</SelectItem>
              {kpiData.map(p => (
                <SelectItem key={p.name} value={p.name} className="text-white">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(dateFrom || dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-white/50 hover:text-white"
            onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid="button-clear-kpi-dates"
          >
            Clear dates
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            onClick={() => {
              const params = new URLSearchParams();
              if (dateFrom) params.set("dateFrom", dateFrom);
              if (dateTo) params.set("dateTo", dateTo);
              if (selectedDept !== "ALL") params.set("department", selectedDept);
              params.set("person", "ALL");
              try { params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone); } catch {}
              window.open(`/api/admin/kpi/pdf?${params.toString()}`, "_blank");
            }}
            data-testid="button-kpi-pdf-all"
          >
            <Download className="w-4 h-4 mr-1" />
            PDF — All Members
          </Button>
          {selectedPerson !== "ALL" && (
            <Button
              variant="outline"
              size="sm"
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
              onClick={() => {
                const params = new URLSearchParams();
                if (dateFrom) params.set("dateFrom", dateFrom);
                if (dateTo) params.set("dateTo", dateTo);
                if (selectedDept !== "ALL") params.set("department", selectedDept);
                params.set("person", selectedPerson);
                try { params.set("tz", Intl.DateTimeFormat().resolvedOptions().timeZone); } catch {}
                window.open(`/api/admin/kpi/pdf?${params.toString()}`, "_blank");
              }}
              data-testid="button-kpi-pdf-individual"
            >
              <Download className="w-4 h-4 mr-1" />
              PDF — {selectedPerson}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <Target className="w-8 h-8 text-[#00CFE8]" />
            <div>
              <p className="text-2xl font-bold text-white" data-testid="text-kpi-total-assigned">{totals.tasksAssigned}</p>
              <p className="text-white/50 text-xs">Total Assigned</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-[#28C76F]" />
            <div>
              <p className="text-2xl font-bold text-[#28C76F]" data-testid="text-kpi-total-completed">{totals.tasksCompleted}</p>
              <p className="text-white/50 text-xs">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#FF9F43]" />
            <div>
              <p className="text-2xl font-bold text-[#FF9F43]" data-testid="text-kpi-ontime-rate">{onTimeRate}%</p>
              <p className="text-white/50 text-xs">On-Time Rate</p>
              {/* Phase 4: badge naming the grading basis. Only renders when
                  the toggle is on, since "board date" is the historical
                  default and doesn't need labelling. */}
              {gradingMode === "internal" && (
                <p
                  className="text-xs text-[#7367F0] mt-0.5"
                  title="On-time / missed counts grade closures against each item's internal handoff deadline (currentInternalDueDate) when set, falling back to the board due date. Configure in Settings → Live Refresh & Performance."
                  data-testid="badge-kpi-grading-mode"
                >
                  Graded vs internal date
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-[#FF9F43]" />
            <div>
              <p className="text-2xl font-bold text-[#FF9F43]" data-testid="text-kpi-onoriginal-rate">{onOriginalRate}%</p>
              <p className="text-white/50 text-xs">On Original Due</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4 flex items-center gap-3">
            <RotateCcw className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-2xl font-bold text-red-400" data-testid="text-kpi-total-returned">{totals.timesReturned}</p>
              <p className="text-white/50 text-xs">Returned for Amendments</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-kpi">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/60 font-medium px-4 py-3 whitespace-nowrap">Name</th>
                  <th className="text-left text-white/60 font-medium px-3 py-3 whitespace-nowrap">Function</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Assigned</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">With Now</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Completed</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Overdue</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">On Time</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Missed</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap" title="Met on or before the original due date set when the item was created">On Orig.</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap" title="Missed against the original due date">Missed Orig.</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Returned</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Received</th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Handed Off</th>
                  <th
                    className="text-center text-[#FF9F43]/80 font-medium px-3 py-3 whitespace-nowrap"
                    title="Number of times this person had an item taken off them by someone else with reason 'Insufficient progress'. Only counts override reassignments tagged as insufficient progress; other override reasons (correct owner, unavailable, rebalancing, scope change, other) do not affect this column."
                  >
                    Pulled (Stalled)
                  </th>
                  <th className="text-center text-white/60 font-medium px-3 py-3 whitespace-nowrap">Avg Time</th>
                  <th className="text-center text-[#2563eb]/80 font-medium px-3 py-3 whitespace-nowrap border-l border-white/10">Sub-Tasks</th>
                  <th className="text-center text-[#2563eb]/80 font-medium px-3 py-3 whitespace-nowrap">Sub Done</th>
                  <th className="text-center text-[#2563eb]/80 font-medium px-3 py-3 whitespace-nowrap">Sub Overdue</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((p) => {
                  const completionRate = p.tasksAssigned > 0
                    ? Math.round((p.tasksCompleted / p.tasksAssigned) * 100)
                    : 0;
                  return (
                    <tr key={p.name} className="border-b border-white/5 hover:bg-white/3" data-testid={`kpi-row-${p.name}`}>
                      <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                        {hasPermission("view_employee_dossier") ? (
                          <Link
                            href={`/hr-dossier?tab=employee-dossier&person=${encodeURIComponent(p.name)}`}
                            className="text-cyan-300 hover:text-cyan-200 hover:underline"
                            data-testid={`link-employee-dossier-${p.name}`}
                            title="Open Employee Dossier for this person"
                          >
                            {p.name}
                          </Link>
                        ) : (
                          p.name
                        )}
                      </td>
                      <td className="px-3 py-3 text-white/50 whitespace-nowrap">{p.teamFunction}</td>
                      <td className="px-3 py-3 text-center text-white">{p.tasksAssigned}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.tasksCurrentlyWith > 0 ? "text-[#00CFE8] font-medium" : "text-white/30"}>
                          {p.tasksCurrentlyWith}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-[#28C76F]">{p.tasksCompleted}</span>
                        {p.tasksAssigned > 0 && (
                          <span className="text-white/30 text-xs ml-1">({completionRate}%)</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.tasksOverdue > 0 ? "text-red-400 font-medium" : "text-white/30"}>
                          {p.tasksOverdue}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.deadlinesMet > 0 ? "text-[#28C76F]" : "text-white/30"}>
                          {p.deadlinesMet}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.deadlinesMissed > 0 ? "text-red-400" : "text-white/30"}>
                          {p.deadlinesMissed}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={(p.deadlinesMetByOriginal || 0) > 0 ? "text-[#28C76F]" : "text-white/30"} data-testid={`kpi-met-original-${p.name}`}>
                          {p.deadlinesMetByOriginal || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={(p.deadlinesMissedByOriginal || 0) > 0 ? "text-red-400" : "text-white/30"} data-testid={`kpi-missed-original-${p.name}`}>
                          {p.deadlinesMissedByOriginal || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.timesReturnedForAmendments > 0 ? "text-[#FF9F43] font-medium" : "text-white/30"}>
                          {p.timesReturnedForAmendments}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-white/70">{p.tasksHandedTo}</td>
                      <td className="px-3 py-3 text-center text-white/70">{p.tasksHandedFrom}</td>
                      <td className="px-3 py-3 text-center" data-testid={`kpi-pulled-stalled-${p.name}`}>
                        <span className={(p.tasksReassignedAwayInsufficientProgress || 0) > 0 ? "text-[#FF9F43] font-medium" : "text-white/30"}>
                          {p.tasksReassignedAwayInsufficientProgress || 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-white/70 whitespace-nowrap">{formatHours(p.avgHandlingTimeHours)}</td>
                      <td className="px-3 py-3 text-center border-l border-white/10">
                        <span className={p.subTasksAssigned > 0 ? "text-[#2563eb]" : "text-white/30"}>{p.subTasksAssigned || 0}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.subTasksCompleted > 0 ? "text-[#28C76F]" : "text-white/30"}>{p.subTasksCompleted || 0}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={p.subTasksOverdue > 0 ? "text-red-400 font-medium" : "text-white/30"}>{p.subTasksOverdue || 0}</span>
                      </td>
                    </tr>
                  );
                })}
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={17} className="px-4 py-8 text-center text-white/30">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10 mt-4">
        <CardContent className="p-4">
          <h3 className="text-white/80 text-sm font-medium mb-2">Column Descriptions:</h3>
          <ul className="text-white/50 text-xs space-y-1 grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <li><span className="text-white/70 font-medium">Assigned</span> — Total tasks where this person is the owner</li>
            <li><span className="text-white/70 font-medium">With Now</span> — Active tasks currently with this person</li>
            <li><span className="text-white/70 font-medium">Completed</span> — Tasks closed or cancelled (with completion rate)</li>
            <li><span className="text-white/70 font-medium">Overdue</span> — Active tasks past their due date</li>
            <li><span className="text-white/70 font-medium">On Time</span> — Completed tasks that met the deadline</li>
            <li><span className="text-white/70 font-medium">Missed</span> — Completed tasks that missed the deadline</li>
            <li><span className="text-white/70 font-medium">Returned</span> — Times work was sent back for amendments</li>
            <li><span className="text-white/70 font-medium">Received</span> — Tasks handed to this person</li>
            <li><span className="text-white/70 font-medium">Handed Off</span> — Tasks this person passed on to others</li>
            <li><span className="text-white/70 font-medium">Avg Time</span> — Average time holding a task before handing off</li>
            <li><span className="text-[#2563eb]/80 font-medium">Sub-Tasks</span> — Number of sub-tasks assigned to this person</li>
            <li><span className="text-[#2563eb]/80 font-medium">Sub Done</span> — Sub-tasks completed by this person</li>
            <li><span className="text-[#2563eb]/80 font-medium">Sub Overdue</span> — Pending sub-tasks past their due date</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function DeletedItemsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const deletedQuery = useQuery<{ data: any[] }>({
    queryKey: ["/api/action-items/deleted"],
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/action-items/${id}/restore`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Restored", description: "Action item has been restored." });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items/deleted"] });
      queryClient.invalidateQueries({ queryKey: ["/api/action-items"] });
    },
    onError: (err: any) => {
      showFromApiError("Error", err);
    },
  });

  const items = deletedQuery.data?.data || [];

  return (
    <Card className="border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-red-400" />
          Deleted Items
          {items.length > 0 && (
            <Badge variant="secondary" className="text-xs">{items.length}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {deletedQuery.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-white/40 text-sm py-4 text-center">No deleted items.</p>
        ) : (
          <div className="space-y-2">
            {items.map((item: any) => (
              <div
                key={item.id}
                className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/10"
                style={{ background: "rgba(255,255,255,0.03)" }}
                data-testid={`deleted-item-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{item.title}</p>
                  <p className="text-white/40 text-xs">
                    {item.authority_lane} · {item.status}
                    {item.meeting_name && ` · ${item.meeting_name}`}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-green-400 border-green-400/30 hover:bg-green-400/10 shrink-0"
                  onClick={() => restoreMutation.mutate(item.id)}
                  disabled={restoreMutation.isPending}
                  data-testid={`button-restore-${item.id}`}
                >
                  <Archive className="h-3 w-3 mr-1" />
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Reminder Schedule tab ──────────────────────────────────────────────────
interface ReminderScheduleConfig {
  predueOffsetsDays: number[];
  updatedAt: string;
  updatedBy: string;
  reason: string;
  overdueCadenceDays: number[];
  overdueFinalDay: number;
  overdueMdCcDay: number;
  maxOffsets: number;
  maxOffsetDays: number;
  clockStopStatuses: string[];
}

interface ReminderLogRow {
  id: string;
  actionItemId: string | null;
  subAssignmentId: string | null;
  slot: string;
  phase: string;
  sentAt: string;
  recipientsTo: string[];
  recipientsCc: string[];
  success: boolean;
  errorMessage: string | null;
  result: "sent" | "failed" | "skipped" | null;
  skipReason: string | null;
  slotKind: "pre_due" | "overdue" | "final" | null;
  cadenceDay: number | null;
  targetType: "action_item" | "sub_assignment" | null;
  targetId: string | null;
  manualRetryOfId: string | null;
  manualRetryBy: string | null;
}

interface ReminderLogGroupedRow {
  actionItemId: string;
  actionItemTitle: string | null;
  actionItemRef: string | null;
  actionItemStatus: string | null;
  totalCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  lastSentAt: string;
  lastResult: "sent" | "failed" | "skipped";
  lastSlot: string | null;
  lastSlotKind: "pre_due" | "overdue" | "final" | null;
  lastCadenceDay: number | null;
  lastSkipReason: string | null;
  lastErrorMessage: string | null;
}

interface ReminderLogPage {
  mode?: "flat" | "grouped";
  data: ReminderLogRow[] | ReminderLogGroupedRow[];
  total: number;
  page: number;
  page_size: number;
  summary: {
    total: number;
    sent_24h: number;
    failed_24h: number;
    skipped_24h: number;
    sent_7d: number;
    failed_7d: number;
    skipped_7d: number;
    smtp_health: string;
    smtp_last_error: string | null;
    smtp_health_at: string | null;
    scheduler_last_scan_at: string | null;
    scheduler_next_scan_at: string | null;
    scheduler_interval_ms: number;
    retention_days: number;
  };
}

const RESULT_OPTIONS: { value: "sent" | "failed" | "skipped"; label: string }[] = [
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "skipped", label: "Skipped" },
];

const SLOT_KIND_OPTIONS: { value: "pre_due" | "overdue" | "final"; label: string }[] = [
  { value: "pre_due", label: "Pre-due" },
  { value: "overdue", label: "Overdue" },
  { value: "final", label: "Final" },
];

const TARGET_TYPE_OPTIONS: { value: "action_item" | "sub_assignment"; label: string }[] = [
  { value: "action_item", label: "Action item" },
  { value: "sub_assignment", label: "Sub-task" },
];

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86400_000).toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatRelativeFromNow(iso: string | null): string {
  if (!iso) return "—";
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return "—";
  const diffMs = target - Date.now();
  const absSec = Math.round(Math.abs(diffMs) / 1000);
  const future = diffMs >= 0;
  if (absSec < 5) return future ? "any moment" : "just now";
  if (absSec < 60) return future ? `in ${absSec}s` : `${absSec}s ago`;
  const min = Math.round(absSec / 60);
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return future ? `in ${hrs}h` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

const SMTP_HEALTH_STYLE: Record<string, { label: string; cls: string }> = {
  ok:            { label: "OK",            cls: "bg-green-900/40 text-green-300 border-green-400/30" },
  failing:       { label: "Failing",       cls: "bg-red-900/40 text-red-300 border-red-400/30" },
  auth_failing:  { label: "Auth failing",  cls: "bg-red-900/40 text-red-300 border-red-400/30" },
  unknown:       { label: "Unknown",       cls: "bg-white/10 text-white/60 border-white/15" },
};

function ReminderScheduleTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();
  const [, setLocation] = useLocation();
  const [draftOffsets, setDraftOffsets] = useState<number[]>([]);
  const [newOffset, setNewOffset] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [stepUpOpen, setStepUpOpen] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPw, setShowPw] = useState<boolean>(false);

  const cfgQuery = useQuery<{ data: ReminderScheduleConfig }>({
    queryKey: ["/api/admin/reminder-schedule"],
  });

  // R2 — filters with smart defaults: last 7 days, all results, page 1, 50/page.
  // Defaults are captured once at mount via useState lazy init so the
  // "filtersAreDefault" comparison is stable across day-rollover.
  const initialDefaults = useMemo(() => ({ from: isoDaysAgo(7), to: todayIso() }), []);
  const [logFrom, setLogFrom] = useState<string>(initialDefaults.from);
  const [logTo, setLogTo] = useState<string>(initialDefaults.to);
  const [logResults, setLogResults] = useState<Set<"sent" | "failed" | "skipped">>(() => new Set());
  const [logSlotKinds, setLogSlotKinds] = useState<Set<"pre_due" | "overdue" | "final">>(() => new Set());
  const [logTargetTypes, setLogTargetTypes] = useState<Set<"action_item" | "sub_assignment">>(() => new Set());
  const [logSearch, setLogSearch] = useState<string>("");
  const [logSearchDraft, setLogSearchDraft] = useState<string>("");
  const [logPage, setLogPage] = useState<number>(1);
  const [logPageSize, setLogPageSize] = useState<number>(50);
  // R4 — flat (one row per send) vs grouped (one row per action item).
  const [logViewMode, setLogViewMode] = useState<"flat" | "grouped">("flat");
  // R6 — manual retry: dialog state + step-up password (separate from the
  //      schedule-save dialog above so the two never collide).
  const [retryLogId, setRetryLogId] = useState<string | null>(null);
  const [retryPassword, setRetryPassword] = useState<string>("");
  const [retryShowPw, setRetryShowPw] = useState<boolean>(false);
  const retryMutation = useMutation({
    mutationFn: async (vars: { id: string; confirmPassword: string }) => {
      return apiRequest("POST", `/api/admin/reminder-schedule/log/${vars.id}/retry`, {
        confirmPassword: vars.confirmPassword,
      });
    },
    onSuccess: (data: any) => {
      toast({
        title: data?.sent ? "Reminder re-sent" : "Retry attempted",
        description: data?.sent
          ? `Delivered to ${(data?.recipients ?? []).join(", ") || "recipients"}.`
          : (data?.reason || "Send did not succeed — see the new log row for details."),
        variant: data?.sent ? "default" : "destructive",
      });
      setRetryLogId(null);
      setRetryPassword("");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reminder-schedule/log"] });
    },
    onError: (err: any) => {
      showFromApiError(err, "Manual retry failed");
    },
  });

  const logFiltersKey = useMemo(() => ({
    from: logFrom,
    to: logTo,
    result: Array.from(logResults).sort().join(","),
    slot_kind: Array.from(logSlotKinds).sort().join(","),
    target_type: Array.from(logTargetTypes).sort().join(","),
    q: logSearch.trim(),
    page: logPage,
    page_size: logPageSize,
    view: logViewMode,
  }), [logFrom, logTo, logResults, logSlotKinds, logTargetTypes, logSearch, logPage, logPageSize, logViewMode]);

  const logQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (logFiltersKey.from) params.set("from", logFiltersKey.from);
    if (logFiltersKey.to) params.set("to", logFiltersKey.to);
    if (logFiltersKey.result) params.set("result", logFiltersKey.result);
    if (logFiltersKey.slot_kind) params.set("slot_kind", logFiltersKey.slot_kind);
    if (logFiltersKey.target_type) params.set("target_type", logFiltersKey.target_type);
    if (logFiltersKey.q) params.set("q", logFiltersKey.q);
    if (logFiltersKey.view === "grouped") params.set("group_by", "action_item");
    params.set("page", String(logFiltersKey.page));
    params.set("page_size", String(logFiltersKey.page_size));
    return params.toString();
  }, [logFiltersKey]);

  const logQuery = useQuery<ReminderLogPage>({
    queryKey: ["/api/admin/reminder-schedule/log", logFiltersKey],
    queryFn: async () => {
      const res = await fetch(`/api/admin/reminder-schedule/log?${logQueryString}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Failed to load reminder log: ${res.status}`);
      return res.json();
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const toggleSetValue = <T,>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, value: T) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
    setLogPage(1);
  };

  const resetLogFilters = () => {
    // Snap to fresh "today" on reset (so a session left open overnight
    // still resets to the current day's window) — independent of the
    // immutable initialDefaults used by filtersAreDefault.
    setLogFrom(isoDaysAgo(7));
    setLogTo(todayIso());
    setLogResults(new Set());
    setLogSlotKinds(new Set());
    setLogTargetTypes(new Set());
    setLogSearch("");
    setLogSearchDraft("");
    setLogPage(1);
  };

  const filtersAreDefault = useMemo(() => (
    logFrom === initialDefaults.from &&
    logTo === initialDefaults.to &&
    logResults.size === 0 &&
    logSlotKinds.size === 0 &&
    logTargetTypes.size === 0 &&
    logSearch.trim() === ""
  ), [logFrom, logTo, logResults, logSlotKinds, logTargetTypes, logSearch, initialDefaults]);

  const cfg = cfgQuery.data?.data;

  // Hydrate the draft when the config arrives or after a successful save.
  useEffect(() => {
    if (cfg && draftOffsets.length === 0) {
      setDraftOffsets([...cfg.predueOffsetsDays].sort((a, b) => b - a));
    }
  }, [cfg]);

  const updateMutation = useMutation({
    mutationFn: async (payload: { predueOffsetsDays: number[]; reason: string; confirmPassword: string }) => {
      return apiRequest("PUT", "/api/admin/reminder-schedule", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reminder-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/reminder-schedule/log"] });
      setStepUpOpen(false);
      setConfirmPassword("");
      setReason("");
      toast({ title: "Reminder schedule saved", description: "New pre-due offsets are now in effect." });
    },
    onError: (err: any) => {
      showFromApiError("Failed to save reminder schedule", err);
    },
  });

  const addOffset = () => {
    const n = parseInt(newOffset.trim(), 10);
    if (!Number.isFinite(n) || n < 1 || n > (cfg?.maxOffsetDays ?? 365)) {
      toast({ title: "Invalid offset", description: `Must be a whole number between 1 and ${cfg?.maxOffsetDays ?? 365} days.`, variant: "destructive" });
      return;
    }
    if (draftOffsets.includes(n)) {
      toast({ title: "Duplicate", description: `${n} day${n === 1 ? "" : "s"} is already in the list.`, variant: "destructive" });
      return;
    }
    if (draftOffsets.length >= (cfg?.maxOffsets ?? 10)) {
      toast({ title: "Too many offsets", description: `Maximum ${cfg?.maxOffsets ?? 10} pre-due offsets allowed.`, variant: "destructive" });
      return;
    }
    setDraftOffsets([...draftOffsets, n].sort((a, b) => b - a));
    setNewOffset("");
  };

  const removeOffset = (n: number) => {
    setDraftOffsets(draftOffsets.filter((x) => x !== n));
  };

  const isDirty = useMemo(() => {
    if (!cfg) return false;
    const a = [...cfg.predueOffsetsDays].sort((x, y) => x - y).join(",");
    const b = [...draftOffsets].sort((x, y) => x - y).join(",");
    return a !== b;
  }, [cfg, draftOffsets]);

  // Defensive clamp: if total shrinks below current page (e.g. external
  // delete or background purge), snap back to the last valid page. Must
  // live ABOVE the early-return blocks below to satisfy Rules of Hooks.
  const _logPageDataForClamp = logQuery.data;
  const _logPageCountForClamp = _logPageDataForClamp
    ? Math.max(1, Math.ceil((_logPageDataForClamp.total ?? 0) / _logPageDataForClamp.page_size))
    : 1;
  useEffect(() => {
    if (_logPageDataForClamp && logPage > _logPageCountForClamp) setLogPage(_logPageCountForClamp);
  }, [_logPageDataForClamp, logPage, _logPageCountForClamp]);

  if (cfgQuery.isLoading) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-8 text-center text-white/60">
          <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin" />
          Loading reminder schedule…
        </CardContent>
      </Card>
    );
  }

  if (!cfg) {
    return (
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-8 text-center text-white/60">
          Failed to load reminder schedule.
        </CardContent>
      </Card>
    );
  }

  const logPageData = logQuery.data;
  const log = logPageData?.data ?? [];
  const logTotal = logPageData?.total ?? 0;
  const logPageCount = logPageData ? Math.max(1, Math.ceil(logTotal / logPageData.page_size)) : 1;
  const effectivePage = Math.min(logPage, logPageCount);
  const logRangeStart = logTotal === 0 ? 0 : (effectivePage - 1) * logPageSize + 1;
  const logRangeEnd = Math.min(effectivePage * logPageSize, logTotal);

  return (
    <div className="space-y-6">
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            Pre-Due Email Reminders
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-white/70 leading-relaxed">
            Configure how many days <strong>before</strong> the due date the system sends an email reminder.
            Defaults are <strong>14 / 7 / 1 days</strong>. The list can have up to {cfg.maxOffsets} entries, each between 1 and {cfg.maxOffsetDays} days.
            Reminders are skipped for items whose status is{" "}
            {cfg.clockStopStatuses.map((s, i) => (
              <span key={s}>
                {i > 0 && (i === cfg.clockStopStatuses.length - 1 ? " or " : ", ")}
                <code className="px-1.5 py-0.5 rounded bg-white/10 text-white/90 text-xs">{s}</code>
              </span>
            ))}
            , or that have been deleted.
          </p>

          <div>
            <Label className="text-white/80 text-sm mb-2 block">Current pre-due offsets (days before due)</Label>
            <div className="flex flex-wrap gap-2 items-center min-h-[44px] p-3 rounded-md bg-white/5 border border-white/10">
              {draftOffsets.length === 0 ? (
                <span className="text-white/40 text-sm">No pre-due reminders configured.</span>
              ) : (
                draftOffsets.map((n) => (
                  <Badge
                    key={n}
                    variant="secondary"
                    className="bg-cyan-900/40 text-cyan-200 border border-cyan-400/30 pl-3 pr-1 py-1"
                    data-testid={`badge-reminder-offset-${n}`}
                  >
                    {n} day{n === 1 ? "" : "s"} before
                    <button
                      type="button"
                      onClick={() => removeOffset(n)}
                      className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded hover:bg-white/10"
                      data-testid={`button-remove-offset-${n}`}
                      aria-label={`Remove ${n}-day reminder`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))
              )}
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex-1 max-w-[200px]">
              <Label className="text-white/80 text-sm mb-2 block">Add another offset</Label>
              <Input
                type="number"
                min={1}
                max={cfg.maxOffsetDays}
                value={newOffset}
                onChange={(e) => setNewOffset(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOffset(); } }}
                placeholder="e.g. 3"
                className="bg-white/5 border-white/10 text-white"
                data-testid="input-new-offset"
              />
            </div>
            <Button
              variant="outline"
              onClick={addOffset}
              disabled={draftOffsets.length >= cfg.maxOffsets}
              className="text-white border-white/20"
              data-testid="button-add-offset"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          <div>
            <Label className="text-white/80 text-sm mb-2 block">Reason for change (optional)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you changing the reminder schedule?"
              maxLength={500}
              className="bg-white/5 border-white/10 text-white"
              data-testid="textarea-reminder-reason"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              onClick={() => setStepUpOpen(true)}
              disabled={!isDirty || updateMutation.isPending}
              data-testid="button-save-reminder-schedule"
            >
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save changes
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDraftOffsets([...cfg.predueOffsetsDays].sort((a, b) => b - a))}
              disabled={!isDirty || updateMutation.isPending}
              className="text-white/60"
              data-testid="button-revert-reminder-schedule"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Revert
            </Button>
            <div className="text-xs text-white/40 ml-auto" data-testid="text-reminder-last-updated">
              Last updated {new Date(cfg.updatedAt).toLocaleString()} by <span className="text-white/60">{cfg.updatedBy}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Past-Due Cadence (fixed)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-white/70 leading-relaxed mb-4">
            Once an item passes its due date, the system sends overdue reminders on a fixed cadence.
            This is hard-coded and not configurable from the UI:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cfg.overdueCadenceDays.map((d) => {
              const isFinal = d === cfg.overdueFinalDay;
              const isMdCc = d === cfg.overdueMdCcDay;
              return (
                <div
                  key={d}
                  className="rounded-md border border-white/10 bg-white/5 p-3"
                  data-testid={`card-overdue-cadence-${d}`}
                >
                  <div className="text-xl font-bold text-white">+{d} day{d === 1 ? "" : "s"}</div>
                  <div className="text-xs text-white/60 mt-1">
                    {isFinal ? "Final reminder" : "Standard reminder"}
                  </div>
                  {isMdCc && (
                    <div className="text-xs text-amber-300 mt-1">CC: MD admins</div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <List className="w-5 h-5 text-white/60" />
            Recent Reminder Send Log
            {logPageData && (
              <span className="ml-2 text-xs text-white/50 font-normal" data-testid="text-reminder-log-total">
                ({logTotal.toLocaleString()} {logTotal === 1 ? "row" : "rows"} match)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* R3 — Status strip */}
          {logPageData?.summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="region-reminder-log-status">
              <div className="rounded-md border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-1.5 text-xs text-white/50 uppercase tracking-wide">
                  <Activity className="w-3.5 h-3.5" /> Last 24h
                </div>
                <div className="mt-1.5 flex items-baseline gap-3 text-sm" data-testid="text-status-24h">
                  <span className="text-green-300"><strong className="text-green-200">{logPageData.summary.sent_24h}</strong> sent</span>
                  <span className="text-amber-200"><strong>{logPageData.summary.skipped_24h}</strong> skipped</span>
                  <span className="text-red-300"><strong>{logPageData.summary.failed_24h}</strong> failed</span>
                </div>
              </div>
              <div className="rounded-md border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-1.5 text-xs text-white/50 uppercase tracking-wide">
                  <Activity className="w-3.5 h-3.5" /> Last 7d
                </div>
                <div className="mt-1.5 flex items-baseline gap-3 text-sm" data-testid="text-status-7d">
                  <span className="text-green-300"><strong className="text-green-200">{logPageData.summary.sent_7d}</strong> sent</span>
                  <span className="text-amber-200"><strong>{logPageData.summary.skipped_7d}</strong> skipped</span>
                  <span className="text-red-300"><strong>{logPageData.summary.failed_7d}</strong> failed</span>
                </div>
              </div>
              <div className="rounded-md border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-1.5 text-xs text-white/50 uppercase tracking-wide">
                  <Server className="w-3.5 h-3.5" /> SMTP health
                </div>
                <div className="mt-1.5 flex items-center gap-2" data-testid="text-status-smtp">
                  {(() => {
                    const s = SMTP_HEALTH_STYLE[logPageData.summary.smtp_health] ?? SMTP_HEALTH_STYLE.unknown;
                    return (
                      <Badge className={s.cls} title={logPageData.summary.smtp_last_error ?? ""}>
                        {s.label}
                      </Badge>
                    );
                  })()}
                  {logPageData.summary.smtp_health_at && (
                    <span className="text-xs text-white/40">{formatRelativeFromNow(logPageData.summary.smtp_health_at)}</span>
                  )}
                </div>
                {logPageData.summary.smtp_last_error && (
                  <div className="mt-1 text-sm text-red-300/80 truncate" title={logPageData.summary.smtp_last_error}>
                    {logPageData.summary.smtp_last_error}
                  </div>
                )}
              </div>
              <div className="rounded-md border border-white/10 bg-white/5 p-3">
                <div className="flex items-center gap-1.5 text-xs text-white/50 uppercase tracking-wide">
                  <Clock className="w-3.5 h-3.5" /> Scheduler
                </div>
                <div className="mt-1.5 text-sm text-white/80" data-testid="text-status-scheduler">
                  Next scan {formatRelativeFromNow(logPageData.summary.scheduler_next_scan_at)}
                </div>
                <div className="mt-0.5 text-sm text-white/40">
                  Retention: {logPageData.summary.retention_days} days
                </div>
              </div>
            </div>
          )}

          {/* Filter bar */}
          <div className="rounded-md border border-white/10 bg-white/5 p-3 space-y-3" data-testid="region-reminder-log-filters">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-white/70 text-xs mb-1 block">From</Label>
                <Input
                  type="date"
                  value={logFrom}
                  onChange={(e) => { setLogFrom(e.target.value); setLogPage(1); }}
                  className="bg-white/5 border-white/10 text-white h-9 w-[160px]"
                  data-testid="input-log-from"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs mb-1 block">To</Label>
                <Input
                  type="date"
                  value={logTo}
                  onChange={(e) => { setLogTo(e.target.value); setLogPage(1); }}
                  className="bg-white/5 border-white/10 text-white h-9 w-[160px]"
                  data-testid="input-log-to"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-white/70 text-xs mb-1 block">Search recipient (To / CC)</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    type="text"
                    value={logSearchDraft}
                    onChange={(e) => setLogSearchDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); setLogSearch(logSearchDraft); setLogPage(1); }
                    }}
                    onBlur={() => { if (logSearchDraft !== logSearch) { setLogSearch(logSearchDraft); setLogPage(1); } }}
                    placeholder="email or domain…"
                    className="bg-white/5 border-white/10 text-white h-9 pl-8"
                    data-testid="input-log-search"
                  />
                </div>
              </div>
              <div className="flex items-end gap-1">
                <div className="flex flex-col">
                  <Label className="text-white/70 text-xs mb-1 block">View</Label>
                  <div className="inline-flex rounded border border-white/10 overflow-hidden h-9" role="group" aria-label="View mode">
                    {(["flat", "grouped"] as const).map((mode) => {
                      const active = logViewMode === mode;
                      return (
                        <button
                          key={mode}
                          type="button"
                          aria-pressed={active}
                          onClick={() => { setLogViewMode(mode); setLogPage(1); }}
                          className={`px-3 text-xs transition ${active ? "bg-cyan-600/30 text-cyan-100" : "bg-white/5 text-white/60 hover:bg-white/10"}`}
                          data-testid={`button-view-mode-${mode}`}
                        >
                          {mode === "flat" ? "Flat" : "Grouped"}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={resetLogFilters}
                  disabled={filtersAreDefault}
                  className="h-9 text-white/60"
                  data-testid="button-log-reset-filters"
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Reset
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    let tz = "";
                    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch {}
                    const sep = logQueryString ? "&" : "";
                    const url = `/api/admin/reminder-schedule/log/export?${logQueryString}${tz ? `${sep}tz=${encodeURIComponent(tz)}` : ""}`;
                    window.location.assign(url);
                  }}
                  disabled={logTotal === 0 || logQuery.isFetching}
                  className="h-9 text-white/60"
                  title="Download current view as Excel (.xlsx)"
                  data-testid="button-log-export-xlsx"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Export
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <div className="flex items-center gap-1.5 text-white/60">
                <Filter className="w-3.5 h-3.5" />
                Result:
                {RESULT_OPTIONS.map((o) => {
                  const active = logResults.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleSetValue(setLogResults, o.value)}
                      className={`px-2 py-0.5 rounded border transition ${active ? "bg-cyan-600/30 border-cyan-400/50 text-cyan-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                      data-testid={`button-filter-result-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 text-white/60">
                Slot:
                {SLOT_KIND_OPTIONS.map((o) => {
                  const active = logSlotKinds.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleSetValue(setLogSlotKinds, o.value)}
                      className={`px-2 py-0.5 rounded border transition ${active ? "bg-cyan-600/30 border-cyan-400/50 text-cyan-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                      data-testid={`button-filter-slot-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-1.5 text-white/60">
                Target:
                {TARGET_TYPE_OPTIONS.map((o) => {
                  const active = logTargetTypes.has(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleSetValue(setLogTargetTypes, o.value)}
                      className={`px-2 py-0.5 rounded border transition ${active ? "bg-cyan-600/30 border-cyan-400/50 text-cyan-100" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                      data-testid={`button-filter-target-${o.value}`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Table */}
          {logQuery.isLoading ? (
            <div className="text-white/60 text-sm py-8 text-center">
              <Loader2 className="w-4 h-4 mx-auto mb-2 animate-spin" /> Loading…
            </div>
          ) : logQuery.isError ? (
            <div className="text-red-300 text-sm py-6 text-center" data-testid="text-reminder-log-error">
              Failed to load reminder log. Try again or reset filters.
            </div>
          ) : log.length === 0 ? (
            <div className="text-white/40 text-sm py-8 text-center" data-testid="text-no-reminder-log">
              {filtersAreDefault
                ? (logViewMode === "grouped"
                    ? "No action items received reminders in the last 7 days."
                    : "No reminders have been fired in the last 7 days.")
                : (logViewMode === "grouped"
                    ? "No action items match the current filters."
                    : "No log entries match the current filters.")}
            </div>
          ) : logViewMode === "grouped" ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-reminder-log-grouped">
                <thead className="text-white/60 text-xs uppercase">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-2">Action item</th>
                    <th className="text-right p-2">Sent</th>
                    <th className="text-right p-2">Skipped</th>
                    <th className="text-right p-2">Failed</th>
                    <th className="text-left p-2">Last activity</th>
                    <th className="text-left p-2">Last result</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {(log as ReminderLogGroupedRow[]).map((g) => {
                    const onRowClick = () => setLocation(`/action-items/${g.actionItemId}`);
                    const lastDetail = g.lastResult === "skipped"
                      ? (g.lastSkipReason ? g.lastSkipReason.replace(/_/g, " ") : null)
                      : g.lastResult === "failed"
                        ? (g.lastErrorMessage || "send failed")
                        : null;
                    const lastSlotLabel = g.lastSlotKind && g.lastCadenceDay != null
                      ? `${g.lastSlotKind === "pre_due" ? "Pre-due" : g.lastSlotKind === "final" ? "Final" : "Overdue"} · ${g.lastSlotKind === "pre_due" ? "−" : "+"}${g.lastCadenceDay}d`
                      : g.lastSlot;
                    return (
                      <tr
                        key={g.actionItemId}
                        className="border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors focus:bg-white/5 focus:outline-none"
                        onClick={onRowClick}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(); } }}
                        role="button"
                        tabIndex={0}
                        title="Open action item"
                        data-testid={`row-reminder-grouped-${g.actionItemId}`}
                      >
                        <td className="p-2 max-w-[420px]">
                          <div className="flex items-center gap-1.5">
                            {g.actionItemRef && (
                              <span className="text-xs text-cyan-300/80 font-mono">{g.actionItemRef}</span>
                            )}
                            <span className="text-white/90 truncate" title={g.actionItemTitle ?? ""}>
                              {g.actionItemTitle ?? <span className="text-white/40 italic">— untitled —</span>}
                            </span>
                            <ExternalLink className="w-3 h-3 text-white/30 shrink-0" />
                          </div>
                          {g.actionItemStatus && (
                            <div className="text-sm text-white/40 mt-0.5">Status: {g.actionItemStatus}</div>
                          )}
                        </td>
                        <td className="p-2 text-right text-green-300 tabular-nums" data-testid={`text-grouped-sent-${g.actionItemId}`}>{g.sentCount}</td>
                        <td className="p-2 text-right text-amber-200 tabular-nums" data-testid={`text-grouped-skipped-${g.actionItemId}`}>{g.skippedCount}</td>
                        <td className="p-2 text-right text-red-300 tabular-nums" data-testid={`text-grouped-failed-${g.actionItemId}`}>{g.failedCount}</td>
                        <td className="p-2 whitespace-nowrap">
                          <div className="text-white/80">{formatRelativeFromNow(g.lastSentAt)}</div>
                          {lastSlotLabel && (
                            <div className="text-sm text-white/40">{lastSlotLabel}</div>
                          )}
                        </td>
                        <td className="p-2 whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {g.lastResult === "sent" ? (
                              <Badge className="bg-green-900/40 text-green-300 border-green-400/30 w-fit">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
                              </Badge>
                            ) : g.lastResult === "skipped" ? (
                              <Badge variant="secondary" className="bg-amber-900/30 text-amber-200 border-amber-400/30 w-fit">
                                Skipped
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-red-900/30 text-red-300 border-red-400/30 w-fit">
                                <AlertCircle className="w-3 h-3 mr-1" /> Failed
                              </Badge>
                            )}
                            {lastDetail && (
                              <span className={`text-sm break-words max-w-[260px] ${g.lastResult === "skipped" ? "text-amber-200/80" : "text-red-300/80"}`}>
                                {lastDetail}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-white/60 text-xs uppercase">
                  <tr className="border-b border-white/10">
                    <th className="text-left p-2">When</th>
                    <th className="text-left p-2">Slot</th>
                    <th className="text-left p-2">Target</th>
                    <th className="text-left p-2">To</th>
                    <th className="text-left p-2">CC</th>
                    <th className="text-left p-2">Result</th>
                    <th className="text-right p-2 w-[1%] whitespace-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="text-white/80">
                  {(log as ReminderLogRow[]).map((r) => {
                    const slotIcon = r.slotKind === "pre_due"
                      ? <CalendarClock className="w-3.5 h-3.5 text-cyan-400/80" />
                      : r.slotKind === "final"
                        ? <AlertCircle className="w-3.5 h-3.5 text-red-400/80" />
                        : <Clock className="w-3.5 h-3.5 text-amber-400/80" />;
                    const slotLabel = r.slotKind && r.cadenceDay != null
                      ? `${r.slotKind === "pre_due" ? "Pre-due" : r.slotKind === "final" ? "Final" : "Overdue"} · ${r.slotKind === "pre_due" ? "−" : "+"}${r.cadenceDay}d`
                      : r.slot;
                    const targetLabel = r.targetType === "sub_assignment" || r.subAssignmentId
                      ? "Sub-task"
                      : r.targetType === "action_item" || r.actionItemId
                        ? "Action item"
                        : "—";
                    const targetId = r.targetId ?? r.subAssignmentId ?? r.actionItemId ?? "";
                    const navigateTarget = r.actionItemId; // sub-task rows also carry their parent action_item_id
                    const isSent = r.result === "sent" || (r.result == null && r.success);
                    const isSkipped = r.result === "skipped";
                    const detailMsg = isSkipped
                      ? (r.skipReason ? r.skipReason.replace(/_/g, " ") : null)
                      : (!isSent ? (r.errorMessage || "send failed (no detail)") : null);
                    const onRowClick = navigateTarget
                      ? () => setLocation(`/action-items/${navigateTarget}`)
                      : undefined;
                    return (
                      <tr
                        key={r.id}
                        className={`border-b border-white/5 ${onRowClick ? "cursor-pointer hover:bg-white/5 transition-colors focus:bg-white/5 focus:outline-none" : ""}`}
                        onClick={onRowClick}
                        onKeyDown={onRowClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(); } } : undefined}
                        role={onRowClick ? "button" : undefined}
                        tabIndex={onRowClick ? 0 : undefined}
                        title={onRowClick ? "Open action item" : undefined}
                        data-testid={`row-reminder-log-${r.id}`}
                      >
                        <td className="p-2 whitespace-nowrap text-white/60">{new Date(r.sentAt).toLocaleString()}</td>
                        <td className="p-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5" title={r.slot}>
                            {slotIcon}{slotLabel}
                          </span>
                        </td>
                        <td className="p-2 text-xs max-w-[320px]" title={targetId}>
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-1.5 text-white/50 text-sm">
                              <span className="uppercase tracking-wide">{targetLabel}</span>
                              {r.actionItemRef && (
                                <span className="font-mono text-cyan-300/80">{r.actionItemRef}</span>
                              )}
                              {onRowClick && <ExternalLink className="w-3 h-3 text-white/30" />}
                            </div>
                            <span className="text-white/90 truncate" title={r.actionItemTitle ?? ""}>
                              {r.actionItemTitle ?? <span className="text-white/40 italic">— untitled —</span>}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 text-xs break-all max-w-[280px]">{Array.isArray(r.recipientsTo) ? r.recipientsTo.join(", ") : ""}</td>
                        <td className="p-2 text-xs break-all max-w-[220px]">{Array.isArray(r.recipientsCc) ? r.recipientsCc.join(", ") : ""}</td>
                        <td className="p-2 whitespace-nowrap">
                          {isSent ? (
                            <Badge className="bg-green-900/40 text-green-300 border-green-400/30">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Sent
                            </Badge>
                          ) : isSkipped ? (
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="secondary" className="bg-amber-900/30 text-amber-200 border-amber-400/30 w-fit" title={r.skipReason ?? ""}>
                                Skipped
                              </Badge>
                              {detailMsg && (
                                <span className="text-sm text-amber-200/80 break-words max-w-[260px]" data-testid={`text-skip-reason-${r.id}`}>
                                  {detailMsg}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <Badge variant="secondary" className="bg-red-900/30 text-red-300 border-red-400/30 w-fit" title={r.errorMessage || ""}>
                                <AlertCircle className="w-3 h-3 mr-1" /> Failed
                              </Badge>
                              {detailMsg && (
                                <span className="text-sm text-red-300/80 break-words max-w-[260px]" data-testid={`text-error-message-${r.id}`}>
                                  {detailMsg}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td
                          className="p-2 text-right whitespace-nowrap"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          {!isSent && !r.manualRetryOfId ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-cyan-300 hover:text-cyan-200 hover:bg-cyan-900/20"
                              onClick={() => { setRetryLogId(r.id); setRetryPassword(""); }}
                              title="Re-send this reminder now"
                              data-testid={`button-retry-${r.id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Retry
                            </Button>
                          ) : r.manualRetryOfId ? (
                            <span className="text-sm text-white/40 italic" title={`Manual retry of ${r.manualRetryOfId}`}>retry</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Retention banner */}
          <div className="flex items-center gap-1.5 text-sm text-white/40 pt-2" data-testid="text-reminder-log-retention">
            <Clock className="w-3 h-3" />
            <span>
              Reminder events are retained for {logPageData?.summary?.retention_days ?? 90} days. Older entries are purged automatically. Use Export to keep an offline copy.
            </span>
          </div>

          {/* Pagination */}
          {logPageData && logTotal > 0 && (
            <div className="flex items-center justify-between text-xs text-white/60 pt-2 border-t border-white/5" data-testid="region-reminder-log-pagination">
              <div data-testid="text-reminder-log-range">
                Showing <span className="text-white/80">{logRangeStart.toLocaleString()}–{logRangeEnd.toLocaleString()}</span> of <span className="text-white/80">{logTotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>Rows per page</span>
                <Select value={String(logPageSize)} onValueChange={(v) => { setLogPageSize(Number(v)); setLogPage(1); }}>
                  <SelectTrigger className="h-7 w-[78px] bg-white/5 border-white/10 text-white" data-testid="select-log-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[25, 50, 100, 200].map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`option-page-size-${n}`}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                  disabled={logPage <= 1 || logQuery.isFetching}
                  className="h-7 px-2 text-white/70"
                  data-testid="button-log-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="tabular-nums" data-testid="text-log-page-indicator">
                  Page {logPage} / {logPageCount}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLogPage((p) => Math.min(logPageCount, p + 1))}
                  disabled={logPage >= logPageCount || logQuery.isFetching}
                  className="h-7 px-2 text-white/70"
                  data-testid="button-log-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* R6 — Manual retry step-up dialog. Separate from the schedule-save
              dialog below so the two never share state. */}
      <Dialog open={retryLogId !== null} onOpenChange={(o) => { if (!o) { setRetryLogId(null); setRetryPassword(""); } }}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm password to re-send reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-white/70">
              The reminder will be sent again immediately using the same recipients the scheduler would resolve right now (so any responsible-party changes since the original send will be reflected). A new audit log row will be written linked to the original.
            </p>
            <Label className="text-white/80 text-sm">Your password</Label>
            <div className="relative">
              <Input
                type={retryShowPw ? "text" : "password"}
                value={retryPassword}
                onChange={(e) => setRetryPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white pr-10"
                data-testid="input-retry-confirm-password"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && retryPassword && retryLogId) {
                    retryMutation.mutate({ id: retryLogId, confirmPassword: retryPassword });
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setRetryShowPw(!retryShowPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                data-testid="button-toggle-retry-password-visibility"
              >
                {retryShowPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setRetryLogId(null); setRetryPassword(""); }}
              className="text-white/60"
              data-testid="button-cancel-retry"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { if (retryLogId) retryMutation.mutate({ id: retryLogId, confirmPassword: retryPassword }); }}
              disabled={!retryPassword || retryMutation.isPending}
              data-testid="button-confirm-retry"
            >
              {retryMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Confirm & Re-send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stepUpOpen} onOpenChange={(o) => { if (!o) { setStepUpOpen(false); setConfirmPassword(""); } }}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm password to save reminder schedule</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-white/80 text-sm">Your password</Label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-white/5 border-white/10 text-white pr-10"
                data-testid="input-reminder-confirm-password"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && confirmPassword) {
                    updateMutation.mutate({ predueOffsetsDays: draftOffsets, reason, confirmPassword });
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                data-testid="button-toggle-password-visibility"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setStepUpOpen(false); setConfirmPassword(""); }}
              className="text-white/60"
              data-testid="button-cancel-reminder-stepup"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateMutation.mutate({ predueOffsetsDays: draftOffsets, reason, confirmPassword })}
              disabled={!confirmPassword || updateMutation.isPending}
              data-testid="button-confirm-reminder-stepup"
            >
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirm & Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chairman AI — WhatsApp channel enrolment (P2, blocker A2)
// ---------------------------------------------------------------------------

/**
 * What an administrator can see and do here, and what they deliberately cannot.
 *
 * CAN: enrol a number against an existing, active Board Tracker account, read
 * back the one-time code once, re-issue it, see the lifecycle state, replace
 * the number, and revoke access.
 *
 * WHAT ENROLMENT DOES *NOT* DO, because the previous wording here implied
 * otherwise: it does not change, require or consult the account's application
 * role. Stage 2 moved the capability onto the channel row (`execRole`), so a
 * super_admin and a member enrolled here receive the SAME organisation-wide
 * read-only capability and neither one's web privileges are altered. Enrolling
 * somebody is not a promotion and it is not a demotion.
 *
 * CANNOT: read a number that has already been enrolled. The server stores only
 * a salted hash and the last four digits, so there is nothing to display — and
 * that is the point rather than a limitation. A screenshot of this screen is
 * not a disclosure of the Chairman's personal number.
 */
type ChairmanChannel = {
  id: string;
  channel: string;
  last4: string | null;
  masked: string;
  principalUserId: string;
  /** The channel's execution capability. Never the account's web role. */
  execRole: string | null;
  label: string | null;
  state: "pending" | "expired" | "locked" | "active" | "revoked";
  verifiedAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  codeExpiresAt: string | null;
  attemptsUsed: number;
  attemptsAllowed: number;
  lastSeenAt: string | null;
  createdBy: string | null;
  createdAt: string | null;
};

const CHANNEL_STATE_STYLE: Record<ChairmanChannel["state"], { label: string; className: string; hint: string }> = {
  active: {
    label: "Active",
    className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    hint: "This number can ask questions and receive answers.",
  },
  pending: {
    label: "Awaiting code",
    className: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    hint: "Grants nothing yet. The Chairman must send the one-time code from this number.",
  },
  expired: {
    label: "Code expired",
    className: "bg-slate-500/15 text-slate-300 border-slate-500/30",
    hint: "The code timed out. Re-issue to try again.",
  },
  locked: {
    label: "Locked",
    className: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    hint: "Too many wrong codes. Re-issue to unlock — and find out who was guessing.",
  },
  revoked: {
    label: "Revoked",
    className: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    hint: "Access withdrawn. This row is kept as the audit record.",
  },
};

function ChairmanChannelsTab() {
  const { toast } = useToast();
  const { showFromApiError } = useErrorDialog();

  const [showEnrol, setShowEnrol] = useState(false);
  const [principalUserId, setPrincipalUserId] = useState("");
  const [address, setAddress] = useState("");
  const [label, setLabel] = useState("Chairman");
  const [enrolPassword, setEnrolPassword] = useState("");

  const [actionOn, setActionOn] = useState<{ channel: ChairmanChannel; kind: "revoke" | "replace" | "reissue" } | null>(null);
  const [actionPassword, setActionPassword] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [replaceAddress, setReplaceAddress] = useState("");

  /**
   * The one-time code, held in component state ONLY.
   *
   * Never written to localStorage, never put in a query cache key, never
   * refetched. Closing the panel loses it, which is correct: if it is lost it
   * should be re-issued rather than recovered, and a code that can be recovered
   * from a browser is a code that outlives the conversation it was meant for.
   */
  const [issuedCode, setIssuedCode] = useState<{ code: string; last4: string | null } | null>(null);

  const channelsQuery = useQuery<{ data: ChairmanChannel[] }>({
    queryKey: ["/api/admin/chairman-channels"],
  });
  const channels = channelsQuery.data?.data ?? [];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/chairman-channels"] });

  const closeAction = () => {
    setActionOn(null);
    setActionPassword("");
    setActionReason("");
    setReplaceAddress("");
  };

  const enrolMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/chairman-channels", {
        principalUserId: principalUserId.trim(),
        address: address.trim(),
        label: label.trim(),
        confirmPassword: enrolPassword,
      }),
    onSuccess: async (res: any) => {
      const body = await res.json();
      invalidate();
      setShowEnrol(false);
      setAddress("");
      setEnrolPassword("");
      setIssuedCode({ code: body?.data?.enrolmentCode ?? "", last4: body?.data?.last4 ?? null });
    },
    onError: (err: any) => showFromApiError("Could not enrol that number", err),
  });

  const reissueMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/admin/chairman-channels/${id}/reissue`, {
        confirmPassword: actionPassword,
      }),
    onSuccess: async (res: any) => {
      const body = await res.json();
      invalidate();
      const last4 = actionOn?.channel.last4 ?? null;
      closeAction();
      setIssuedCode({ code: body?.data?.enrolmentCode ?? "", last4 });
    },
    onError: (err: any) => showFromApiError("Could not re-issue the code", err),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/admin/chairman-channels/${id}/revoke`, {
        confirmPassword: actionPassword,
        reason: actionReason.trim(),
      }),
    onSuccess: () => {
      invalidate();
      closeAction();
      toast({ title: "WhatsApp access revoked", description: "The next message from that number is refused." });
    },
    onError: (err: any) => showFromApiError("Could not revoke", err),
  });

  const replaceMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/admin/chairman-channels/${id}/replace`, {
        address: replaceAddress.trim(),
        reason: actionReason.trim(),
        confirmPassword: actionPassword,
      }),
    onSuccess: async (res: any) => {
      const body = await res.json();
      invalidate();
      closeAction();
      setIssuedCode({
        code: body?.data?.enrolmentCode ?? "",
        last4: body?.data?.channel?.last4 ?? null,
      });
    },
    onError: (err: any) => showFromApiError("Could not replace the number", err),
  });

  const busy =
    enrolMutation.isPending ||
    reissueMutation.isPending ||
    revokeMutation.isPending ||
    replaceMutation.isPending;

  return (
    <div className="space-y-6" data-testid="tab-chairman-channels">
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Chairman WhatsApp Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-white/70">
          <p>
            Enrolling a number lets it ask Board Tracker questions over WhatsApp and receive answers
            drawn from the Chairman's organisation-wide read. It grants no ability to change anything.
          </p>
          <p className="text-white/50">
            Enrolment takes two people. You create it here and Board Tracker gives you a one-time
            code <span className="text-white/80">once</span>. Deliver that code out of band — by
            voice, in person, anything that is not the number being enrolled — and the Chairman sends
            it back <span className="text-white/80">from that phone</span>. Until they do, the
            enrolment grants nothing.
          </p>
          <p className="text-white/50">
            Board Tracker stores the number only as a salted hash plus its last four digits. It is
            not shown here because it is not kept.
          </p>
          <Button
            onClick={() => setShowEnrol(true)}
            disabled={busy}
            data-testid="button-enrol-chairman-channel"
          >
            <Plus className="w-4 h-4 mr-2" />
            Enrol a number
          </Button>
        </CardContent>
      </Card>

      {issuedCode && (
        <Card className="bg-amber-500/10 border-amber-500/30" data-testid="panel-enrolment-code">
          <CardHeader>
            <CardTitle className="text-amber-200 flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              One-time code — shown once
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              className="font-mono text-3xl tracking-[0.3em] text-white"
              data-testid="text-enrolment-code"
            >
              {issuedCode.code}
            </div>
            <p className="text-sm text-amber-200/80">
              For the number ending {issuedCode.last4 ?? "????"}. Deliver it by a route that is not
              this WhatsApp number, then ask the Chairman to send it from that phone. It expires in
              15 minutes and is not recoverable — if it is lost, re-issue.
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(issuedCode.code);
                  toast({ title: "Copied" });
                }}
                data-testid="button-copy-enrolment-code"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
              <Button variant="ghost" onClick={() => setIssuedCode(null)} data-testid="button-dismiss-enrolment-code">
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white">Enrolled numbers</CardTitle>
        </CardHeader>
        <CardContent>
          {channelsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-white/60">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading…
            </div>
          ) : channels.length === 0 ? (
            <p className="text-white/50" data-testid="text-no-chairman-channels">
              No number is enrolled. The Chairman AI WhatsApp channel is not in use.
            </p>
          ) : (
            <div className="space-y-3">
              {channels.map((c) => {
                const style = CHANNEL_STATE_STYLE[c.state];
                return (
                  <div
                    key={c.id}
                    className="rounded-lg border border-white/10 bg-white/5 p-4"
                    data-testid={`row-chairman-channel-${c.id}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-white text-lg" data-testid={`text-channel-masked-${c.id}`}>
                            {c.masked}
                          </span>
                          <Badge className={style.className} data-testid={`badge-channel-state-${c.id}`}>
                            {style.label}
                          </Badge>
                        </div>
                        <div className="text-xs text-white/50 mt-1">{style.hint}</div>
                        <div className="text-xs text-white/40 mt-1">
                          {/* THE CAPABILITY, SHOWN. An administrator must be able to see
                              what a channel may do without inferring it from the
                              account's application role — which after Stage 2 would
                              be the wrong answer. An unrecognised value is rendered as
                              itself and flagged, because that is a row to revoke. */}
                          <span data-testid={`text-channel-exec-role-${c.id}`}>
                            {c.execRole === "exec_readonly"
                              ? "Read-only (organisation-wide)"
                              : `Capability not recognised: ${c.execRole ?? "none"}`}
                          </span>
                          {" · "}
                          {c.label ?? "—"} · enrolled by {c.createdBy ?? "—"}
                          {c.state === "pending" && c.codeExpiresAt
                            ? ` · code expires ${new Date(c.codeExpiresAt).toLocaleString()}`
                            : ""}
                          {c.state === "locked" || c.state === "pending"
                            ? ` · ${c.attemptsUsed}/${c.attemptsAllowed} attempts used`
                            : ""}
                          {c.lastSeenAt ? ` · last used ${new Date(c.lastSeenAt).toLocaleString()}` : ""}
                          {c.revokedAt
                            ? ` · revoked ${new Date(c.revokedAt).toLocaleString()} by ${c.revokedBy ?? "—"}`
                            : ""}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(c.state === "pending" || c.state === "expired" || c.state === "locked") && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => setActionOn({ channel: c, kind: "reissue" })}
                            data-testid={`button-reissue-${c.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Re-issue code
                          </Button>
                        )}
                        {c.state !== "revoked" && (
                          <>
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => setActionOn({ channel: c, kind: "replace" })}
                              data-testid={`button-replace-${c.id}`}
                            >
                              <ArrowRightLeft className="w-4 h-4 mr-2" />
                              Replace number
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={busy}
                              onClick={() => setActionOn({ channel: c, kind: "revoke" })}
                              data-testid={`button-revoke-${c.id}`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Revoke
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enrol */}
      <Dialog open={showEnrol} onOpenChange={(o) => !o && setShowEnrol(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enrol a WhatsApp number</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cc-principal">Board Tracker user ID</Label>
              <Input
                id="cc-principal"
                value={principalUserId}
                onChange={(e) => setPrincipalUserId(e.target.value)}
                placeholder="The Board Tracker account this number will act as"
                data-testid="input-channel-principal"
              />
              <p className="text-xs text-white/50 mt-1">
                Must be an existing, active Board Tracker account. Their application role is not
                changed by enrolling them and is not what this channel is allowed to do.
              </p>
              <p className="text-xs text-white/50 mt-1" data-testid="text-channel-capability-note">
                This channel will be granted one capability:{" "}
                <span className="text-white/70">organisation-wide READ-ONLY access to action items</span>. It
                cannot change anything, and it cannot reach meeting minutes — whatever the account can do
                on the web.
              </p>
            </div>
            <div>
              <Label htmlFor="cc-address">WhatsApp number</Label>
              <Input
                id="cc-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="+971501234567"
                data-testid="input-channel-address"
              />
              <p className="text-xs text-white/50 mt-1">
                Full international format. It is hashed immediately and never displayed again.
              </p>
            </div>
            <div>
              <Label htmlFor="cc-label">Label</Label>
              <Input
                id="cc-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="input-channel-label"
              />
            </div>
            <div>
              <Label htmlFor="cc-password">Your password</Label>
              <Input
                id="cc-password"
                type="password"
                value={enrolPassword}
                onChange={(e) => setEnrolPassword(e.target.value)}
                data-testid="input-channel-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEnrol(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => enrolMutation.mutate()}
              disabled={
                busy || !principalUserId.trim() || !address.trim() || !label.trim() || !enrolPassword
              }
              data-testid="button-confirm-enrol"
            >
              {enrolMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create enrolment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-issue / replace / revoke */}
      <Dialog open={actionOn !== null} onOpenChange={(o) => !o && closeAction()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionOn?.kind === "revoke"
                ? "Revoke WhatsApp access"
                : actionOn?.kind === "replace"
                  ? "Replace the registered number"
                  : "Re-issue the one-time code"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-white/70">
              {actionOn?.kind === "revoke"
                ? `The number ending ${actionOn?.channel.last4 ?? "????"} stops working immediately, including for a message already being processed.`
                : actionOn?.kind === "replace"
                  ? `The number ending ${actionOn?.channel.last4 ?? "????"} is revoked FIRST, then the new one is enrolled as pending. There is no moment when both work.`
                  : "The previous code stops working and the attempt counter is reset."}
            </p>
            {actionOn?.kind === "replace" && (
              <div>
                <Label htmlFor="cc-new-address">New WhatsApp number</Label>
                <Input
                  id="cc-new-address"
                  value={replaceAddress}
                  onChange={(e) => setReplaceAddress(e.target.value)}
                  placeholder="+971501234567"
                  data-testid="input-channel-new-address"
                />
              </div>
            )}
            {(actionOn?.kind === "revoke" || actionOn?.kind === "replace") && (
              <div>
                <Label htmlFor="cc-reason">Reason</Label>
                <Textarea
                  id="cc-reason"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="Handset lost; number ported; role change…"
                  data-testid="input-channel-reason"
                />
                <p className="text-xs text-white/50 mt-1">
                  Recorded in the audit trail. Six months from now this is the only explanation
                  anyone will have.
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="cc-action-password">Your password</Label>
              <Input
                id="cc-action-password"
                type="password"
                value={actionPassword}
                onChange={(e) => setActionPassword(e.target.value)}
                data-testid="input-channel-action-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeAction}>
              Cancel
            </Button>
            <Button
              variant={actionOn?.kind === "revoke" ? "destructive" : "default"}
              disabled={
                busy ||
                !actionPassword ||
                (actionOn?.kind !== "reissue" && !actionReason.trim()) ||
                (actionOn?.kind === "replace" && !replaceAddress.trim())
              }
              onClick={() => {
                if (!actionOn) return;
                if (actionOn.kind === "revoke") revokeMutation.mutate(actionOn.channel.id);
                else if (actionOn.kind === "replace") replaceMutation.mutate(actionOn.channel.id);
                else reissueMutation.mutate(actionOn.channel.id);
              }}
              data-testid="button-confirm-channel-action"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
